import { useEffect, useRef, useState, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { api, apiUpload, urlImagem, comprimirImagem, prepararVideoOS, arquivoEhVideo, validarVideoOS, urlEhVideo, getToken, VIDEO_OS_MAX_SEGUNDOS } from '../lib/api';
import { API_BASE } from '../config';
import { rotuloStatus, rotuloPrioridade, rotuloTipo, dataHora } from '../lib/rotulos';
import { useAuth } from '../lib/auth';
import Loading from '../components/Loading';
import AssinaturaPad from '../components/AssinaturaPad';
import Passos, { Passo } from '../components/Passos';
import SeletorFotos from '../components/SeletorFotos';
import GravadorVideo, { suportaGravador } from '../components/GravadorVideo';
import { linkWhatsapp } from '../lib/whatsapp';
import { usePodeIncluirDescricaoImagem } from '../lib/useConfigEmpresa';

interface ClienteOpt { id: number; nome: string; whatsapp: string | null; email?: string | null; }
// `inativo` só é marcado em runtime para o cliente que já está na OS mas foi desativado.
type ClienteOpcao = ClienteOpt & { inativo?: boolean };

interface Historico {
  id: number; acao: string; status_anterior: string | null; status_novo: string | null; created_at: string;
}
interface HistoricoOrcamento {
  id: number;
  usuario_id: number | null;
  usuario_nome?: string | null;
  valor_anterior: string | number | null;
  valor_novo: string | number | null;
  created_at: string;
}
interface ImagemOS {
  id: number; imagem_url: string; tipo: string; created_at: string; arquivo_tipo?: string | null; descricao?: string | null;
}
interface ChecklistResp {
  id: number; checklist_item_id: number; descricao: string; marcado: boolean | string; observacao: string | null; imagem_url: string | null;
}
interface ChecklistItem {
  id: number;
  descricao: string;
  obrigatorio: boolean | string;
  exige_foto: boolean | string;
  exige_observacao: boolean | string;
  ordem: number;
}
interface ChecklistModelo {
  id: number;
  nome: string;
  itens: ChecklistItem[];
}
// Processo por ETAPAS na execucao da OS - so existe com a permissao
// "INCLUIR ETAPAS NO PROCESSO DE OS" (incluir_etapas_processo_os_habilitado).
interface EtapaTecnico { usuario_id: number; nome: string | null; }
interface EtapaChecklistResp {
  id: number; checklist_item_id: number; descricao: string; marcado: boolean | string;
  observacao: string | null; imagem_url: string | null;
}
interface EtapaOS {
  id: number;
  usuario_id: number | null;
  tecnico_nome: string | null;
  etapa_codigo: string;
  etapa_nome: string;
  ordem: number;
  status: string;
  checklist_modelo_id: number | null;
  checklist_modelo_nome: string | null;
  imagem_url: string | null;
  observacao: string | null;
  iniciado_em_sp: string | null;
  concluido_em_sp: string | null;
  iniciado_por_nome: string | null;
  concluido_por_nome: string | null;
  checklist_respostas: EtapaChecklistResp[];
  atual: boolean;
  liberada: boolean;
  bloqueada_por: { id: number; etapa_nome: string } | null;
}
interface EtapasResumoTecnico {
  usuario_id: number | null;
  nome: string | null;
  total: number;
  concluidas: number;
  etapa_atual_id: number | null;
  etapa_atual_nome: string | null;
  todas_concluidas: boolean;
  finalizado_em_sp: string | null;
}
interface EtapasResumo {
  total: number;
  concluidas: number;
  por_tecnico: EtapasResumoTecnico[];
  todas_concluidas: boolean;
  aguardando_fechamento_gestor: boolean;
}
interface EtapasPayload {
  ordem_servico_id: number;
  etapas: EtapaOS[];
  resumo: EtapasResumo;
  tecnicos_os: EtapaTecnico[];
  checklist_modelos: ChecklistModelo[];
  permissoes: { pode_configurar: boolean; pode_fechar_os: boolean };
}
interface MaterialUso {
  id: number; material_nome: string; tipo: string; quantidade: string | number; valor_unitario: string | number; valor_total: string | number; created_at: string;
}
interface MaterialCatalogo {
  id: number; nome: string; valor_unitario: string | number; quantidade_atual: string | number;
}
interface UsuarioOpt { id: number; nome: string; perfil?: string }
interface OSDetalheData {
  id: number; codigo: string; status: string; tipo_os: string; prioridade: string;
  ativo_id: number | null; ativo_categoria_id: number | null;
  ativo_modelo?: string | null;
  ativo_marca?: string | null;
  ativo_patrimonio?: string | null;
  ativo_numero_serie?: string | null;
  tecnico_id: number | null; data_agendada: string | null; hora_agendada: string | null;
  lembrete_pausado?: boolean | string;
  inicio_atendimento: string | null; fim_atendimento: string | null;
  inicio_atendimento_sp?: string | null; fim_atendimento_sp?: string | null;
  unidade_nome: string; piso_nome: string; local_nome: string; ativo_nome: string | null;
  veiculo_id?: number | null;
  veiculo_placa?: string | null;
  veiculo_marca?: string | null;
  veiculo_modelo?: string | null;
  veiculo_ano?: number | null;
  veiculo_km_atual?: number | null;
  solicitante_nome: string | null; tecnico_nome: string | null;
  avaria: string | null; descricao: string | null; observacao: string | null; observacao_conclusao: string | null; tempo_total_minutos: number | null;
  tempo_total_calculado_minutos?: number | null;
  tecnico_valor_hora?: number | null;
  mao_obra_valor?: number | null;
  orcamento_valor?: string | number | null;
  servico_id?: number | null;
  servico_descricao?: string | null;
  servico_valor?: string | number | null;
  os_servicos?: ServicoOsItem[];
  os_servicos_total?: number | null;
  os_servicos_sem_preco?: number;
  orcamento_historico?: HistoricoOrcamento[];
  materiais_total?: number | null;
  custo_total?: number | null;
  assinatura_tecnico_url: string | null; assinatura_cliente_url: string | null;
  cliente_id: number | null;
  cliente: { id: number; nome: string; whatsapp: string | null; email?: string | null } | null;
  documento_pdf_url: string | null;
  assinatura_link: string | null;
  assinatura_cliente_url_publica: string | null;
  assinatura_tecnico_url_publica: string | null;
  historico: Historico[];
  imagens: ImagemOS[];
  checklist: ChecklistResp[];
  materiais: MaterialUso[];
  etapas?: EtapaOS[];
  etapas_resumo?: EtapasResumo;
  etapas_habilitado?: boolean;
  pode_fechar_os_etapas?: boolean;
}

function marcado(v: boolean | string): boolean {
  return v === true || v === 'true' || v === '1';
}

interface ServicoOpcao {
  id: number;
  rotulo: string;
  faixa_contratual: string | null;
  valor_unitario: string | number | null;
  observacao: string | null;
}

// Servicos lancados na OS (varios por OS) - permissao "CRIACAO DE OS
// SIMPLIFICADA" ou "SERVICOS".
interface ServicoOsItem {
  id: number;
  servico_id: number | null;
  descricao: string;
  quantidade: number;
  valor_unitario: string | number | null;
  valor_total: string | number | null;
  sem_preco: boolean;
}

interface SugestaoServicoOs {
  descricao: string;
  valor_unitario: string | number | null;
}

// Formas que o endpoint aceita por item:
//   { id }         linha que ja existe: preserva descricao e valor gravados
//   { servico_id } copia da tabela de precos agora
//   { descricao }  servico livre, digitado (nao depende de nenhum outro modulo)
type ItemServicoEnvio =
  | { id: number; quantidade: number }
  | { servico_id: number; quantidade: number }
  | { descricao: string; valor_unitario: number | null; quantidade: number };

interface ServicosOsPayload {
  itens: ServicoOsItem[];
  total: number | null;
  sem_preco: number;
  opcoes: ServicoOpcao[];
  sugestoes: SugestaoServicoOs[];
  filtro?: {
    tipo_os: string | null;
    tipo_os_original: string | null;
    btus: number | null;
    origem_capacidade: string | null;
    por_capacidade: boolean;
    tem_tabela_precos: boolean;
  };
  pode_ver_valores: boolean;
}

function moeda(v: unknown): string {
  const n = Number(v);
  const seguro = Number.isFinite(n) ? n : 0;
  return 'R$ ' + seguro.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function moedaMascara(centavos: string): string {
  if (!centavos) return '';
  return moeda(Number(centavos) / 100);
}

const ESTADOS_VISIVEIS = ['aberto', 'em_andamento', 'interrompido', 'aguardando_aprovacao', 'concluido', 'cancelado'];
// Etapas mostradas por pagina na execucao (pedido: de 4 em 4).
const ETAPAS_POR_PAGINA = 4;

function podeMudarEstado(atual: string, destino: string): boolean {
  if (atual === destino) return false;
  if (['concluido', 'cancelado'].includes(atual)) return false;
  if (destino === 'cancelado') return true;
  if (atual === 'aberto') return destino === 'em_andamento';
  if (atual === 'em_andamento') return ['interrompido', 'aguardando_aprovacao', 'concluido'].includes(destino);
  if (atual === 'interrompido') return destino === 'em_andamento';
  if (atual === 'aguardando_aprovacao') return destino === 'aberto';
  return false;
}

function rotuloBotaoEstado(atual: string, destino: string): string {
  if (atual === destino) return rotuloStatus(destino);
  if (destino === 'em_andamento') return atual === 'aberto' ? 'Iniciar' : 'Retomar';
  if (destino === 'interrompido') return 'Pausar';
  if (destino === 'aguardando_aprovacao') return 'Enviar p/ aprovação';
  if (atual === 'aguardando_aprovacao' && destino === 'aberto') return 'Agendar novamente';
  if (destino === 'concluido') return 'Concluir';
  if (destino === 'cancelado') return 'Cancelar';
  return rotuloStatus(destino);
}

// Data/hora atual de Sao Paulo no formato do input datetime-local (YYYY-MM-DDTHH:mm).
function agoraSaoPauloInput(): string {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date());
  const g = (t: string) => p.find((x) => x.type === t)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
}

function paraInputDataHora(raw?: string | null, sp?: string | null): string {
  const fonte = (sp || raw || '').trim();
  if (!fonte) return '';
  const diretoBr = fonte.match(/^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})/);
  if (diretoBr) {
    const [, dia, mes, ano, hora, minuto] = diretoBr;
    return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
  }
  let normalizado = fonte.includes('T') ? fonte : fonte.replace(' ', 'T');
  normalizado = normalizado.replace(/(\.\d{3})\d+/, '$1');
  normalizado = normalizado.replace(/([+-]\d{2})$/, '$1:00');
  normalizado = normalizado.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const d = new Date(normalizado);
  const diretoIso = fonte.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  const p = (n: number) => String(n).padStart(2, '0');
  if (isNaN(d.getTime()) && diretoIso) {
    const [, ano, mes, dia, hora, minuto] = diretoIso;
    return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
  }
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function OSDetalhe() {
  const { id } = useParams();
  const { isPremium, isOrcamentoOsDisponivel, user } = useAuth();
  const podeVerMateriaisOs = user?.pode_ver_materiais_os !== false;
  const podeVerValoresMonetarios = !user?.somente_gestor_supervisor_pode_ver_valores_habilitado
    || ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  // Permissao de empresa "NAO VER CUSTO MAO DE OBRA": esconde valor/hora, mao de
  // obra e o custo/total geral (que soma mao de obra). O backend ja nao manda.
  const ocultarCustoMaoDeObra = Boolean(user?.ocultar_custo_mao_obra_relatorio_chamados);
  const podeUsarOrcamentoOs = isOrcamentoOsDisponivel && podeVerValoresMonetarios;
  const podeAdicionarObsConclusao = user?.pode_adicionar_obs_conclusao_os !== false;
  const tecnicoAssinaturaWhatsappRestrita = user?.perfil === 'tecnico' && Boolean(user?.pode_encaminhar_assinatura_whatsapp);
  const podeTransferirOsWhatsapp = user?.perfil !== 'tecnico'
    || (!tecnicoAssinaturaWhatsappRestrita && user?.pode_transferir_os_whatsapp !== false);
  const podeEncaminharAssinaturaWhatsapp = user?.perfil !== 'tecnico' || tecnicoAssinaturaWhatsappRestrita;
  const podeExibirEnvioWhatsapp = podeTransferirOsWhatsapp || podeEncaminharAssinaturaWhatsapp;
  const novoFluxoAssinatura = Boolean(user?.alterar_ordem_assinatura_habilitado);
  // Gate do processo por etapas. Com a permissao desligada nada abaixo e usado
  // e a execucao da OS continua identica ao fluxo tradicional.
  const etapasHabilitado = Boolean(user?.incluir_etapas_processo_os_habilitado);
  const ehGestorEtapas = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  // Permissao de empresa "PERMITIR DADOS EQUIPAMENTOS NO DETALHES E EM MATERIAIS".
  // Antes isto era comparacao de string fixa com o nome da empresa e com o e-mail
  // do usuario: trocar qualquer um dos dois derrubava o recurso sem aviso, e
  // nenhuma outra empresa conseguia habilitar.
  const dadosEquipamentoHabilitado = Boolean(user?.dados_equipamento_os_materiais_habilitado);
  // Permissao de empresa "SERVICOS": sem ela a aba Materiais fica exatamente
  // como era, sem campo de servico.
  const servicosHabilitado = Boolean(user?.servicos_habilitado);
  const [os, setOs] = useState<OSDetalheData | null>(null);
  // Permissao de empresa "CRIACAO DE OS SIMPLIFICADA": libera o bloco de
  // servicos lancados (varios por OS, com quantidade e total). Desligada, o
  // bloco nao existe e a aba Materiais fica como era.
  const osSimplificadaHabilitado = Boolean(user?.criacao_os_simplificada_habilitado);
  // "RETIRAR UNIDADE - LOCAL - PISO": a linha "Local" sai do detalhe. Nesse fluxo
  // ela viria vazia ou com a unidade provisionada, que nao diz nada ao tecnico.
  const semUnidadeLocalPiso = Boolean(user?.retirar_unidade_local_piso_habilitado);
  const [servicosOsLista, setServicosOsLista] = useState<ServicosOsPayload | null>(null);
  const [salvandoServicosOs, setSalvandoServicosOs] = useState(false);
  const [novoServicoOs, setNovoServicoOs] = useState({ servico_id: '', descricao: '', valor_unitario: '', quantidade: '1' });
  const [imprimindoOs, setImprimindoOs] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [tipoImagem, setTipoImagem] = useState('execucao');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [descricoesImagens, setDescricoesImagens] = useState<string[]>([]);
  const [videoArquivo, setVideoArquivo] = useState<File | null>(null);
  const [descricaoVideo, setDescricaoVideo] = useState('');
  const podeDescricaoImagem = usePodeIncluirDescricaoImagem();
  const [salvandoDescricaoImg, setSalvandoDescricaoImg] = useState<number | null>(null);
  const [edicaoDescricaoImg, setEdicaoDescricaoImg] = useState<Record<number, string>>({});
  const [validandoVideo, setValidandoVideo] = useState(false);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [enviandoUnico, setEnviandoUnico] = useState<number | 'video' | null>(null);
  const [progresso, setProgresso] = useState(0);
  const [enviouConcluido, setEnviouConcluido] = useState(false);
  const [gravadorAberto, setGravadorAberto] = useState(false);
  const [checklistModelos, setChecklistModelos] = useState<ChecklistModelo[]>([]);
  const [checklistRespostas, setChecklistRespostas] = useState<Record<number, { marcado: boolean; observacao: string; imagem_url: string }>>({});
  const [checklistArquivos, setChecklistArquivos] = useState<Record<number, File | null>>({});
  const [salvandoChecklist, setSalvandoChecklist] = useState(false);
  const [materiaisCatalogo, setMateriaisCatalogo] = useState<MaterialCatalogo[]>([]);
  const [matForm, setMatForm] = useState({ material_id: '', novoNome: '', quantidade: '', valor_unitario: '', novoEstoque: '' });
  const [salvandoMaterial, setSalvandoMaterial] = useState(false);
  const [removendoMaterial, setRemovendoMaterial] = useState<number | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioOpt[]>([]);
  const [agendarAberto, setAgendarAberto] = useState(false);
  const [ag, setAg] = useState({ data_agendada: '', hora_agendada: '', tecnico_id: '', tecnico_ids: [] as number[] });
  const [salvandoAgenda, setSalvandoAgenda] = useState(false);
  const [execForm, setExecForm] = useState({ inicio_atendimento: '', fim_atendimento: '' });
  const [orcamentoForm, setOrcamentoForm] = useState('');
  const [salvandoOrcamento, setSalvandoOrcamento] = useState(false);
  const [obsConclusaoForm, setObsConclusaoForm] = useState('');
  const [salvandoObsConclusao, setSalvandoObsConclusao] = useState(false);
  const [confirma, setConfirma] = useState<{ titulo: string; linhas: string[]; rotulo: string; cor: string; acao: () => void } | null>(null);
  const [etapas, setEtapas] = useState<EtapaOS[]>([]);
  const [etapasResumo, setEtapasResumo] = useState<EtapasResumo | null>(null);
  const [etapasModelos, setEtapasModelos] = useState<ChecklistModelo[]>([]);
  const [etapasTecnicosOs, setEtapasTecnicosOs] = useState<EtapaTecnico[]>([]);
  const [etapasCarregadas, setEtapasCarregadas] = useState(false);
  const [etapaOcupada, setEtapaOcupada] = useState<number | null>(null);
  const [etapaAberta, setEtapaAberta] = useState<number | null>(null);
  const [etapaChecklist, setEtapaChecklist] = useState<Record<number, Record<number, { marcado: boolean; observacao: string; imagem_url: string }>>>({});
  const [etapaObsForm, setEtapaObsForm] = useState<Record<number, string>>({});
  const [etapaChecklistArquivos, setEtapaChecklistArquivos] = useState<Record<string, File | null>>({});
  // Paginacao das etapas: 4 por pagina, uma pagina por tecnico.
  const [etapaPagina, setEtapaPagina] = useState<Record<number, number>>({});
  const [emailClienteForm, setEmailClienteForm] = useState('');
  const [enviandoEmailCliente, setEnviandoEmailCliente] = useState(false);
  const [clientes, setClientes] = useState<ClienteOpt[]>([]);
  const [clienteSel, setClienteSel] = useState('');
  const [novoCli, setNovoCli] = useState({ nome: '', whatsapp: '', email: '' });
  const [mostrarNovoCli, setMostrarNovoCli] = useState(false);
  const [salvandoCli, setSalvandoCli] = useState(false);
  const videoGaleriaRef = useRef<HTMLInputElement>(null);
  const videoCameraRef = useRef<HTMLInputElement>(null);

  async function carregar() {
    setErro('');
    try {
      const data = await api<OSDetalheData>('/os/os_detalhe.php?id=' + id);
      setOs(data);
      aplicarRespostas(data.checklist || []);
    }
    catch (e) { setErro(e instanceof Error ? e.message : String(e)); }
  }

  // Servicos lancados nesta OS + opcoes da tabela de precos. Falha silenciosa,
  // como o resto da aba Materiais: sem isso ela segue funcionando.
  // Disponivel para QUALQUER uma das duas permissoes (OS simplificada OU
  // Servicos) - com Servicos o backend ja filtra as opcoes por capacidade do
  // equipamento.
  async function carregarServicosOsLista() {
    if (!osSimplificadaHabilitado && !servicosHabilitado) return;
    try {
      setServicosOsLista(await api<ServicosOsPayload>('/os/os_servicos.php?ordem_servico_id=' + id));
    } catch {
      setServicosOsLista(null);
    }
  }

  // O endpoint TROCA a lista inteira, entao toda alteracao manda a lista final.
  // As linhas que NAO estao sendo editadas vao por `id`: o backend mantem a
  // descricao e o valor ja gravados. E o que impede dois estragos silenciosos -
  // reprecificar uma OS antiga com o preco atual da tabela, e apagar os valores
  // quando quem edita nao tem permissao para ver valor (recebe a lista com valor
  // nulo e reenviaria esse nulo).
  function listaServicosParaEnvio(excluirItemId?: number): ItemServicoEnvio[] {
    return (servicosOsLista?.itens || [])
      .filter((s) => s.id !== excluirItemId)
      .map((s) => ({ id: s.id, quantidade: s.quantidade }));
  }

  async function salvarServicosOs(itens: ItemServicoEnvio[], mensagem: string) {
    setErro('');
    setMsg('');
    setSalvandoServicosOs(true);
    try {
      await api('/os/os_servicos.php', {
        method: 'PUT',
        body: { ordem_servico_id: Number(id), servicos: itens },
      });
      setMsg(mensagem);
      await Promise.all([carregar(), carregarServicosOsLista()]);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvandoServicosOs(false);
    }
  }

  // Atalho da tabela de precos, quando o tenant tem uma: preenche descricao e
  // valor, que seguem editaveis.
  function escolherServicoDaTabela(valor: string) {
    const o = servicosOsLista?.opcoes.find((x) => String(x.id) === valor);
    if (!o) { setNovoServicoOs({ ...novoServicoOs, servico_id: '' }); return; }
    setNovoServicoOs({
      servico_id: valor,
      descricao: o.rotulo,
      valor_unitario: o.valor_unitario === null ? '' : String(o.valor_unitario),
      quantidade: novoServicoOs.quantidade || '1',
    });
  }

  // Descricao que ja foi usada antes traz o ultimo valor lancado.
  function digitarDescricaoServicoOs(texto: string) {
    const s = servicosOsLista?.sugestoes.find((x) => x.descricao.toLowerCase() === texto.trim().toLowerCase());
    setNovoServicoOs((atual) => ({
      ...atual,
      // Editar o texto solta o vinculo com a tabela de precos: vale o que esta na tela.
      servico_id: '',
      descricao: texto,
      valor_unitario: s && !atual.valor_unitario && s.valor_unitario !== null ? String(s.valor_unitario) : atual.valor_unitario,
    }));
  }

  async function adicionarServicoOs() {
    const descricao = novoServicoOs.descricao.trim();
    if (!descricao) { setErro('Informe a descricao do servico.'); return; }
    const qtd = Number(String(novoServicoOs.quantidade).replace(',', '.'));
    if (!Number.isFinite(qtd) || qtd <= 0) { setErro('Informe uma quantidade maior que zero.'); return; }
    const valorTexto = String(novoServicoOs.valor_unitario).replace(',', '.');
    const valor = valorTexto === '' ? null : Number(valorTexto);
    if (valor !== null && (!Number.isFinite(valor) || valor < 0)) { setErro('Valor do servico invalido.'); return; }

    const servicoId = novoServicoOs.servico_id ? Number(novoServicoOs.servico_id) : null;
    const atuais = listaServicosParaEnvio();
    atuais.push(servicoId !== null
      ? { servico_id: servicoId, quantidade: qtd }
      : { descricao, valor_unitario: valor, quantidade: qtd });

    await salvarServicosOs(atuais, 'Servico adicionado a OS.');
    setNovoServicoOs({ servico_id: '', descricao: '', valor_unitario: '', quantidade: '1' });
  }

  useEffect(() => { carregar(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!os) return;
    setExecForm({
      inicio_atendimento: paraInputDataHora(os.inicio_atendimento, os.inicio_atendimento_sp),
      fim_atendimento: os.fim_atendimento ? paraInputDataHora(os.fim_atendimento, os.fim_atendimento_sp) : agoraSaoPauloInput(),
    });
  }, [os]);
  useEffect(() => {
    const atual = os?.orcamento_valor;
    if (atual === null || atual === undefined || atual === '') {
      setOrcamentoForm('');
      return;
    }
    setOrcamentoForm(String(Math.round(Number(atual) * 100)));
  }, [os]);
  useEffect(() => {
    setObsConclusaoForm(os?.observacao_conclusao || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [os?.id]);
  // Finalização: já traz o e-mail cadastrado do cliente, sem digitar de novo.
  useEffect(() => {
    setEmailClienteForm(os?.cliente?.email || '');
  }, [os?.id, os?.cliente?.email]);

  useEffect(() => {
    if (!isPremium || !os) {
      setChecklistModelos([]);
      return;
    }
    // checklist da OS: pela categoria do equipamento E pelo tipo de chamado
    api<ChecklistModelo[]>('/checklist/por_os.php?os_id=' + os.id)
      .then(setChecklistModelos)
      .catch(() => setChecklistModelos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, os?.id, os?.tipo_os, os?.ativo_categoria_id]);

  // Catálogo de materiais (liberado em todos os planos) para adicionar à OS.
  useEffect(() => {
    if (!podeVerMateriaisOs) {
      setMateriaisCatalogo([]);
      return;
    }
    api<MaterialCatalogo[]>('/estoque/materiais.php').then(setMateriaisCatalogo).catch(() => {});
  }, [podeVerMateriaisOs]);

  // Usuários (para agendar/atribuir o chamado a alguém).
  useEffect(() => {
    api<UsuarioOpt[]>('/usuarios/usuarios.php').then(setUsuarios).catch(() => {});
  }, []);

  // Clientes (para envio da OS por WhatsApp).
  useEffect(() => {
    api<ClienteOpt[]>('/cadastros/clientes.php?somente_ativos=1').then(setClientes).catch(() => {});
  }, []);
  useEffect(() => { if (os?.cliente_id) setClienteSel(String(os.cliente_id)); }, [os?.cliente_id]);

  // Fonte de verdade da tela de etapas. O GET tambem SEMEIA as 12 etapas na
  // primeira abertura da execucao - a criacao da OS nunca e tocada.
  function aplicarEtapas(payload: EtapasPayload) {
    setEtapas(payload.etapas || []);
    setEtapasResumo(payload.resumo || null);
    setEtapasTecnicosOs(payload.tecnicos_os || []);
    if (payload.checklist_modelos) setEtapasModelos(payload.checklist_modelos);
    const mapa: Record<number, Record<number, { marcado: boolean; observacao: string; imagem_url: string }>> = {};
    const obs: Record<number, string> = {};
    (payload.etapas || []).forEach((et) => {
      const porItem: Record<number, { marcado: boolean; observacao: string; imagem_url: string }> = {};
      (et.checklist_respostas || []).forEach((r) => {
        porItem[r.checklist_item_id] = {
          marcado: marcado(r.marcado),
          observacao: r.observacao || '',
          imagem_url: r.imagem_url || '',
        };
      });
      mapa[et.id] = porItem;
      obs[et.id] = et.observacao || '';
    });
    setEtapaChecklist(mapa);
    setEtapaObsForm(obs);
    // Abre direto na pagina onde esta a etapa atual de cada tecnico.
    const paginas: Record<number, number> = {};
    (payload.etapas || []).forEach((et) => {
      if (!et.atual) return;
      const chave = et.usuario_id ?? 0;
      paginas[chave] = Math.floor((et.ordem - 1) / ETAPAS_POR_PAGINA);
    });
    setEtapaPagina(paginas);
    setEtapasCarregadas(true);
  }

  async function carregarEtapas() {
    if (!etapasHabilitado || !os) return;
    try {
      const payload = await api<EtapasPayload>('/os/os_etapas.php?ordem_servico_id=' + os.id);
      aplicarEtapas(payload);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    carregarServicosOsLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, servicosHabilitado, osSimplificadaHabilitado]);

  useEffect(() => {
    if (!etapasHabilitado || !os?.id) { setEtapasCarregadas(false); return; }
    carregarEtapas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapasHabilitado, os?.id]);

  function setRespostaEtapa(etapaId: number, itemId: number, patch: Partial<{ marcado: boolean; observacao: string; imagem_url: string }>) {
    setEtapaChecklist((atuais) => ({
      ...atuais,
      [etapaId]: {
        ...(atuais[etapaId] || {}),
        [itemId]: { ...(atuais[etapaId]?.[itemId] || { marcado: false, observacao: '', imagem_url: '' }), ...patch },
      },
    }));
  }

  function marcarTodosChecklistEtapa(etapaId: number, itemIds: number[], marcar: boolean) {
    if (!itemIds.length) return;
    setEtapaChecklist((atuais) => {
      const atualEtapa = atuais[etapaId] || {};
      const proximaEtapa = { ...atualEtapa };
      itemIds.forEach((itemId) => {
        proximaEtapa[itemId] = {
          ...(atualEtapa[itemId] || { marcado: false, observacao: '', imagem_url: '' }),
          marcado: marcar,
        };
      });
      return {
        ...atuais,
        [etapaId]: proximaEtapa,
      };
    });
  }

  // Envia uma acao da etapa (configurar/iniciar/concluir/checklist) e aplica a
  // lista devolvida pelo servidor - quem decide o que e valido e o backend.
  async function acaoEtapa(etapa: EtapaOS, acao: string, extra: Record<string, unknown> = {}) {
    if (!os) return;
    setErro(''); setMsg('');
    setEtapaOcupada(etapa.id);
    try {
      const payload = await api<EtapasPayload>('/os/os_etapas.php', {
        method: 'POST',
        body: { ordem_servico_id: os.id, id: etapa.id, acao, ...extra },
      });
      aplicarEtapas(payload);
      // Sem isto o usuário clicava em salvar e a tela não dava sinal nenhum.
      const confirmacao: Record<string, string> = {
        observacao: 'Observação da etapa salva.',
        checklist: 'Checklist da etapa salvo.',
        iniciar: 'Etapa iniciada.',
        concluir: 'Etapa concluída.',
        configurar: 'Checklist vinculado à etapa.',
      };
      if (confirmacao[acao]) setMsg(confirmacao[acao]);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEtapaOcupada(null);
    }
  }

  // Salva o checklist da etapa. Sobe as fotos exigidas antes de gravar, igual
  // ao checklist tradicional da OS.
  async function salvarChecklistEtapa(etapa: EtapaOS) {
    const modelo = etapasModelos.find((m) => m.id === etapa.checklist_modelo_id);
    if (!modelo) return;
    if (!os) return;
    const itens = modelo.itens || [];
    const respostas = [];
    try {
      for (const item of itens) {
        const atual = etapaChecklist[etapa.id]?.[item.id];
        const arquivo = etapaChecklistArquivos[etapa.id + ':' + item.id];
        if (marcado(item.exige_observacao) && atual?.marcado && !(atual?.observacao || '').trim()) {
          setErro('Preencha a observação do item: ' + item.descricao);
          return;
        }
        if (marcado(item.exige_foto) && atual?.marcado && !atual?.imagem_url && !arquivo) {
          setErro('Anexe a foto do item: ' + item.descricao);
          return;
        }
        // A foto escolhida sobe agora e entra na resposta como URL.
        let imagemUrl = atual?.imagem_url || '';
        if (arquivo) {
          const fd = new FormData();
          fd.append('imagem', await comprimirImagem(arquivo));
          fd.append('ordem_servico_id', String(os.id));
          fd.append('id', String(etapa.id));
          fd.append('destino', 'checklist');
          const up = await api<{ imagem_url: string }>('/os/os_etapa_imagem.php', { method: 'POST', body: fd, isForm: true });
          imagemUrl = up.imagem_url;
        }
        respostas.push({
          checklist_item_id: item.id,
          marcado: Boolean(atual?.marcado),
          observacao: atual?.observacao || null,
          imagem_url: imagemUrl || null,
        });
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
      return;
    }
    await acaoEtapa(etapa, 'checklist', { respostas });
    setEtapaChecklistArquivos((atuais) => {
      const limpo = { ...atuais };
      itens.forEach((item) => { delete limpo[etapa.id + ':' + item.id]; });
      return limpo;
    });
  }

  async function enviarImagemEtapa(etapa: EtapaOS, arquivo: File) {
    if (!os) return;
    setErro(''); setMsg('');
    setEtapaOcupada(etapa.id);
    try {
      const fd = new FormData();
      fd.append('imagem', await comprimirImagem(arquivo));
      fd.append('ordem_servico_id', String(os.id));
      fd.append('id', String(etapa.id));
      await api('/os/os_etapa_imagem.php', { method: 'POST', body: fd, isForm: true });
      setMsg('Imagem da etapa enviada.');
      await carregarEtapas();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEtapaOcupada(null);
    }
  }

  // Fluxo final: e-mail de encerramento para o cliente (liberado ao gestor
  // quando nao resta etapa aplicavel pendente).
  async function enviarEmailCliente() {
    if (!os) return;
    setErro(''); setMsg('');
    setEnviandoEmailCliente(true);
    try {
      const resp = await api<{ destinatario: string }>('/os/os_etapas_email_cliente.php', {
        method: 'POST',
        body: { ordem_servico_id: os.id, email: emailClienteForm.trim() || undefined },
      });
      setMsg('E-mail enviado ao cliente: ' + resp.destinatario);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviandoEmailCliente(false);
    }
  }

  async function associarCliente(clienteId: string) {
    setClienteSel(clienteId);
    // O envio por e-mail acompanha o cliente escolhido, como o WhatsApp faz.
    const escolhido = clientes.find((c) => String(c.id) === clienteId);
    if (escolhido) setEmailClienteForm(escolhido.email || '');
    try {
      await api('/os/os_cliente.php', { method: 'POST', body: { ordem_servico_id: Number(id), cliente_id: clienteId ? Number(clienteId) : null } });
      await carregar();
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)); }
  }

  async function cadastrarClienteRapido() {
    setErro(''); setMsg('');
    if (!novoCli.nome.trim()) { setErro('Informe o nome do cliente.'); return; }
    setSalvandoCli(true);
    try {
      const cri = await api<{ id: number }>('/cadastros/clientes.php', {
        method: 'POST',
        body: {
          nome: novoCli.nome.trim(),
          whatsapp: novoCli.whatsapp.trim() || null,
          email: novoCli.email.trim() || null,
        },
      });
      setClientes(await api<ClienteOpt[]>('/cadastros/clientes.php?somente_ativos=1'));
      await associarCliente(String(cri.id));
      if (novoCli.email.trim()) setEmailClienteForm(novoCli.email.trim());
      setNovoCli({ nome: '', whatsapp: '', email: '' });
      setMostrarNovoCli(false);
      setMsg('Cliente cadastrado e vinculado à OS.');
    } catch (e) { setErro(e instanceof Error ? e.message : String(e)); }
    finally { setSalvandoCli(false); }
  }

  const clienteAtual = clientes.find((c) => String(c.id) === clienteSel) || (os?.cliente ?? null);
  const clienteWhats = clienteAtual?.whatsapp || '';
  // A lista só traz clientes ATIVOS. Se o cliente já vinculado a esta OS foi desativado
  // depois, ele entra na lista marcado — senão o select apareceria vazio e o vínculo
  // se perderia visualmente. Um cliente inativo não pode ser escolhido de novo.
  const clientesOpcoes: ClienteOpcao[] = os?.cliente && !clientes.some((c) => c.id === os.cliente?.id)
    ? [{ ...os.cliente, inativo: true }, ...clientes]
    : clientes;

  function enviarPdfWhats() {
    if (!os) return;
    const msg = [
      `Olá! Segue a Ordem de Serviço ${os.codigo}.`,
      `Documento (PDF): ${os.documento_pdf_url || ''}`,
    ].join('\n\n');
    window.open(linkWhatsapp(clienteWhats, msg), '_blank');
  }
  // Envia a OS para CONFERÊNCIA (PDF) + LINK onde o cliente assina remotamente.
  function enviarParaAssinatura() {
    if (!os) return;
    const msg = [
      `Olá! Segue a Ordem de Serviço ${os.codigo} para sua conferência e assinatura.`,
      `Documento (PDF): ${os.documento_pdf_url || ''}`,
      `Para assinar, acesse: ${os.assinatura_link || ''}`,
    ].join('\n\n');
    window.open(linkWhatsapp(clienteWhats, msg), '_blank');
  }

  function enviarSomenteAssinatura() {
    if (!os) return;
    const msg = [
      `OlÃ¡! Segue a Ordem de ServiÃ§o ${os.codigo} para assinatura.`,
      `Para assinar, acesse: ${os.assinatura_link || ''}`,
    ].join('\n\n');
    window.open(linkWhatsapp(clienteWhats, msg), '_blank');
  }

  function aplicarRespostas(respostas: ChecklistResp[]) {
    const mapa: Record<number, { marcado: boolean; observacao: string; imagem_url: string }> = {};
    respostas.forEach((r) => {
      mapa[r.checklist_item_id] = { marcado: marcado(r.marcado), observacao: r.observacao || '', imagem_url: r.imagem_url || '' };
    });
    setChecklistRespostas(mapa);
    setChecklistArquivos({});
  }

  function setResposta(itemId: number, patch: Partial<{ marcado: boolean; observacao: string; imagem_url: string }>) {
    setChecklistRespostas((atuais) => ({
      ...atuais,
      [itemId]: { ...(atuais[itemId] || { marcado: false, observacao: '', imagem_url: '' }), ...patch },
    }));
  }

  function marcarTodosChecklist(marcar: boolean) {
    const itens = checklistModelos.flatMap((modelo) => modelo.itens || []);
    if (!itens.length) return;
    setChecklistRespostas((atuais) => {
      const proximas = { ...atuais };
      itens.forEach((item) => {
        proximas[item.id] = {
          ...(atuais[item.id] || { marcado: false, observacao: '', imagem_url: '' }),
          marcado: marcar,
        };
      });
      return proximas;
    });
  }

  async function executarStatus(status: string) {
    setMsg(''); setErro('');
    try {
      const body: Record<string, unknown> = { id: Number(id), status };
      if (status === 'concluido' && podeAdicionarObsConclusao) {
        body.observacao_conclusao = obsConclusaoForm;
      }
      if (os?.status === 'em_andamento' && status !== 'em_andamento') {
        if (podeEditarInicioManual && execForm.inicio_atendimento) body.inicio_atendimento = execForm.inicio_atendimento;
        if (podeEditarInicioManual && execForm.fim_atendimento) body.fim_atendimento = execForm.fim_atendimento;
      }
      await api('/os/os_atualizar_status.php', { method: 'POST', body });
      carregar();
    }
    catch (e) { setErro(e instanceof Error ? e.message : String(e)); }
  }
  function mudarStatus(status: string) {
    // Trava: fotos/vídeo selecionados só sobem ao clicar "Salvar". Mudar o
    // status antes disso os descartaria em silêncio — avisamos antes.
    if (arquivos.length > 0 || videoArquivo) {
      const ok = window.confirm(
        'Você tem foto(s)/vídeo selecionados que ainda NÃO foram salvos.\n\n' +
        'Clique em "Cancelar" para voltar e salvar primeiro (botão Salvar na aba de fotos),\n' +
        'ou em "OK" para mudar o status mesmo assim e descartar esses anexos.',
      );
      if (!ok) return;
      setArquivos([]);
      setVideoArquivo(null);
    }
    // Processo por etapas ativo: a conclusao e exclusividade do gestor e so
    // depois de todas as etapas atribuidas. Barra antes de abrir o modal.
    if (status === 'concluido' && bloqueioConclusaoEtapas) {
      setErro(bloqueioConclusaoEtapas);
      return;
    }
    if (status === 'concluido') {
      setConfirma({
        titulo: 'Concluir ordem de serviço',
        linhas: [
          'Ao confirmar, o atendimento será dado como FINALIZADO e a OS ficará travada:',
          '•  o status não poderá mais ser alterado;',
          '•  não dá para anexar/excluir fotos, materiais ou checklist;',
          '•  ficará disponível apenas para consulta e impressão (PDF).',
          'Ação definitiva — confirme com o cliente antes de prosseguir.',
        ],
        rotulo: 'Concluir OS',
        cor: 'var(--ok)',
        acao: () => executarStatus('concluido'),
      });
      return;
    }
    if (status === 'cancelado') {
      setConfirma({
        titulo: 'Cancelar ordem de serviço',
        linhas: [
          'O chamado será encerrado sem execução e ficará travado (somente consulta).',
          'Esta ação é definitiva e não pode ser desfeita.',
        ],
        rotulo: 'Cancelar OS',
        cor: 'var(--erro)',
        acao: () => executarStatus('cancelado'),
      });
      return;
    }
    executarStatus(status);
  }
  function abrirAgendar() {
    if (!os) return;
    setAg({
      data_agendada: os.data_agendada || '',
      hora_agendada: os.hora_agendada ? String(os.hora_agendada).slice(0, 5) : '',
      tecnico_id: os.tecnico_id ? String(os.tecnico_id) : '',
      // Com etapas ativo a OS vai para 1 ou mais tecnicos: pre-marca os atuais.
      tecnico_ids: etapasTecnicosOs.length
        ? etapasTecnicosOs.map((t) => t.usuario_id)
        : (os.tecnico_id ? [os.tecnico_id] : []),
    });
    setAgendarAberto(true);
  }
  function acaoEstado(estado: string) {
    if (os && os.status === 'aguardando_aprovacao' && estado === 'aberto') { abrirAgendar(); return; }
    mudarStatus(estado);
  }
  async function salvarAgendamento(e: FormEvent) {
    e.preventDefault();
    setMsg(''); setErro('');
    setSalvandoAgenda(true);
    try {
      await api('/os/os_agendar.php', { method: 'POST', body: {
        ordem_servico_id: Number(id),
        data_agendada: ag.data_agendada || null,
        hora_agendada: ag.hora_agendada || null,
        tecnico_id: ag.tecnico_id ? Number(ag.tecnico_id) : null,
        // Somente com o processo por etapas ativo: 1 ou mais tecnicos. O backend
        // ignora este campo quando a empresa nao tem a permissao.
        ...(etapasHabilitado ? { tecnico_ids: ag.tecnico_ids } : {}),
      } });
      setAgendarAberto(false);
      setMsg('Agendamento salvo.');
      await carregar();
      await carregarEtapas();
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvandoAgenda(false);
    }
  }
  async function salvarOrcamento() {
    if (!os) return;
    setMsg('');
    setErro('');
    setSalvandoOrcamento(true);
    try {
      await api('/os/os_orcamento.php', {
        method: 'POST',
        body: {
          ordem_servico_id: os.id,
          orcamento_valor: moedaMascara(orcamentoForm) || null,
        },
      });
      setMsg('Orcamento atualizado.');
      await carregar();
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvandoOrcamento(false);
    }
  }
  async function salvarObsConclusao() {
    if (!os) return;
    setMsg('');
    setErro('');
    setSalvandoObsConclusao(true);
    try {
      await api('/os/os_atualizar_status.php', {
        method: 'POST',
        body: {
          id: os.id,
          status: os.status,
          observacao_conclusao: obsConclusaoForm,
        },
      });
      setMsg('Observação de conclusão atualizada.');
      await carregar();
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvandoObsConclusao(false);
    }
  }
  const [salvandoLembrete, setSalvandoLembrete] = useState(false);
  async function alternarLembrete(pausado: boolean) {
    if (!os) return;
    setMsg(''); setErro('');
    setSalvandoLembrete(true);
    try {
      await api('/os/os_lembrete_pausar.php', { method: 'POST', body: { ordem_servico_id: os.id, pausado } });
      setOs({ ...os, lembrete_pausado: pausado });
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvandoLembrete(false);
    }
  }
  async function salvarAssinatura(tipo: 'tecnico' | 'cliente', arquivo: File) {
    setMsg('');
    setErro('');
    const fd = new FormData();
    fd.append('ordem_servico_id', String(id));
    fd.append('tipo', tipo);
    fd.append('assinatura', arquivo);
    try {
      await api('/os/os_assinatura.php', { method: 'POST', body: fd, isForm: true });
      setMsg('Assinatura salva.');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }
  // Abre o documento da OS autenticado: o link direto não envia o token (header
  // Authorization), então buscamos com o token e abrimos o resultado numa aba.
  async function abrirDocumento(autoPrint = false) {
    setErro('');
    setImprimindoOs(autoPrint);
    const win = window.open('', '_blank'); // abre no gesto do clique (evita bloqueio de pop-up)
    if (win) win.document.write('Gerando documento da OS...');
    try {
      const res = await fetch(`${API_BASE}/os/os_pdf.php?id=${id}`, { headers: { Authorization: 'Bearer ' + getToken() } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      if (win) {
        win.location.href = url;
        if (autoPrint) {
          try {
            win.addEventListener('load', () => {
              try { win.print(); } catch { /* impressao fica disponivel na aba aberta */ }
            });
          } catch {
            // Alguns navegadores nao deixam observar a aba do PDF. Nesse caso
            // mantemos a aba aberta para imprimir manualmente.
          }
        }
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setImprimindoOs(false);
    } catch {
      if (win) win.close();
      setImprimindoOs(false);
      setErro('Não foi possível abrir o documento da OS.');
    }
  }

  async function adicionarMaterial(e: FormEvent) {
    e.preventDefault();
    setErro(''); setMsg('');
    setSalvandoMaterial(true);
    try {
      let materialId = Number(matForm.material_id);
      const valorNum = podeVerValoresMonetarios && matForm.valor_unitario !== '' ? Number(matForm.valor_unitario) : undefined;
      const qtd = Number(matForm.quantidade);
      if (!qtd || qtd <= 0) throw new Error('Informe a quantidade.');
      if (matForm.material_id === 'novo') {
        const nome = matForm.novoNome.trim();
        if (!nome) throw new Error('Informe o nome do novo material.');
        const estoqueInicial = matForm.novoEstoque !== '' ? Number(matForm.novoEstoque) : 0;
        if (estoqueInicial < qtd) throw new Error('Estoque inicial (' + estoqueInicial + ') menor que a quantidade usada (' + qtd + ').');
        const criado = await api<{ id: number }>('/estoque/materiais.php', { method: 'POST', body: { nome, valor_unitario: valorNum ?? 0, quantidade_atual: estoqueInicial } });
        materialId = criado.id;
      } else {
        // pré-validação: bloqueia antes do POST (o backend 409 é a garantia final).
        const mat = materiaisCatalogo.find((m) => String(m.id) === matForm.material_id);
        if (mat && qtd > Number(mat.quantidade_atual)) {
          throw new Error('Estoque insuficiente para "' + mat.nome + '" (disponível: ' + Number(mat.quantidade_atual) + ').');
        }
      }
      if (!materialId) throw new Error('Selecione um material ou cadastre um novo.');
      await api('/estoque/movimentacoes.php', {
        method: 'POST',
        body: {
          material_id: materialId,
          tipo: 'saida',
          quantidade: qtd,
          ...(valorNum !== undefined ? { valor_unitario: valorNum } : {}),
          ordem_servico_id: Number(id),
        },
      });
      setMatForm({ material_id: '', novoNome: '', quantidade: '', valor_unitario: '', novoEstoque: '' });
      setMsg('Material adicionado à OS.');
      api<MaterialCatalogo[]>('/estoque/materiais.php').then(setMateriaisCatalogo).catch(() => {});
      await carregar();
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setSalvandoMaterial(false);
    }
  }

  async function salvarDescricaoImagem(imagemId: number, descricao: string) {
    setSalvandoDescricaoImg(imagemId);
    try {
      await api('/os/os_imagens.php?id=' + imagemId, { method: 'PUT', body: { descricao } });
      setOs((atual) => atual
        ? { ...atual, imagens: atual.imagens.map((im) => (im.id === imagemId ? { ...im, descricao } : im)) }
        : atual);
      setEdicaoDescricaoImg((atual) => { const { [imagemId]: _omitido, ...resto } = atual; return resto; });
      setMsg('Descrição salva.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvandoDescricaoImg(null);
    }
  }

  async function deletarImagem(imagemId: number) {
    if (!window.confirm('Excluir este anexo?')) return;
    setErro(''); setMsg('');
    try {
      await api('/os/os_imagens.php?id=' + imagemId, { method: 'DELETE' });
      setMsg('Anexo excluído.');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  async function deletarMaterial(movimentacaoId: number) {
    if (!window.confirm('Excluir este material da OS? O estoque sera ajustado automaticamente.')) return;
    setErro(''); setMsg('');
    setRemovendoMaterial(movimentacaoId);
    try {
      await api('/estoque/movimentacoes.php?id=' + movimentacaoId, { method: 'DELETE' });
      setMsg('Material excluido da OS.');
      api<MaterialCatalogo[]>('/estoque/materiais.php').then(setMateriaisCatalogo).catch(() => {});
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setRemovendoMaterial(null);
    }
  }

  async function enviarImagem(e: FormEvent) {
    e.preventDefault();
    const anexos = [...arquivos, ...(videoArquivo ? [videoArquivo] : [])];
    const descricoes = [...descricoesImagens, ...(videoArquivo ? [descricaoVideo] : [])];
    if (!anexos.length) return;
    if (arquivos.length > 5) {
      setErro('Anexe no máximo 5 fotos por envio.');
      return;
    }
    setErro('');
    setMsg('');
    setProgresso(0);
    setEnviouConcluido(false);
    setEnviandoImagem(true);
    try {
      const total = anexos.length;
      for (let i = 0; i < total; i++) {
        const arq = anexos[i];
        const base = (i / total) * 100;
        const fatia = 100 / total;
        // Compressão (não mensurável): reserva 35% da fatia do arquivo.
        const comprimido = arquivoEhVideo(arq) ? await prepararVideoOS(arq) : await comprimirImagem(arq);
        setProgresso(Math.round(base + fatia * 0.35));
        // Upload: 65% restante da fatia, com progresso real do XHR.
        const fd = new FormData();
        fd.append('ordem_servico_id', String(id));
        fd.append('tipo', tipoImagem);
        if (podeDescricaoImagem && descricoes[i]) fd.append('descricao', descricoes[i]);
        fd.append('imagem', comprimido);
        await apiUpload('/os/os_imagens.php', fd, (frac) => {
          setProgresso(Math.round(base + fatia * (0.35 + 0.65 * frac)));
        });
      }
      setProgresso(100);
      setArquivos([]);
      setDescricoesImagens([]);
      setVideoArquivo(null);
      setDescricaoVideo('');
      setMsg(anexos.length > 1 ? 'Anexos enviados.' : 'Anexo enviado.');
      await carregar();
      setEnviouConcluido(true);
    } catch (e2) {
      setErro(e2 instanceof Error ? e2.message : String(e2));
      setProgresso(0);
    } finally {
      setEnviandoImagem(false);
    }
  }

  // Envia so UMA foto/video ja em preview, sem esperar o restante do lote —
  // igual ao app mobile, que ja sobe cada midia assim que ela e escolhida.
  // Depois de subir, a descricao passa a ser editada no grid abaixo (mesmo
  // input+Salvar de qualquer imagem ja existente na OS).
  async function enviarFotoUnica(indice: number) {
    const arquivo = arquivos[indice];
    if (!arquivo) return;
    setErro(''); setMsg('');
    setEnviandoUnico(indice);
    try {
      const comprimido = await comprimirImagem(arquivo);
      const fd = new FormData();
      fd.append('ordem_servico_id', String(id));
      fd.append('tipo', tipoImagem);
      const descricao = descricoesImagens[indice];
      if (podeDescricaoImagem && descricao) fd.append('descricao', descricao);
      fd.append('imagem', comprimido);
      const resp = await api<{ id: number; imagem_url: string; arquivo_tipo?: string | null; descricao?: string | null }>(
        '/os/os_imagens.php', { method: 'POST', body: fd, isForm: true },
      );
      const enviada: ImagemOS = { id: resp.id, imagem_url: resp.imagem_url, tipo: tipoImagem, created_at: new Date().toISOString(), arquivo_tipo: resp.arquivo_tipo, descricao: resp.descricao ?? null };
      setOs((atual) => atual ? ({ ...atual, imagens: [...(atual.imagens || []), enviada] }) : atual);
      setArquivos((atual) => atual.filter((_, i) => i !== indice));
      setDescricoesImagens((atual) => atual.filter((_, i) => i !== indice));
      setMsg('Imagem anexada.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviandoUnico(null);
    }
  }

  async function enviarVideoUnico() {
    if (!videoArquivo) return;
    setErro(''); setMsg('');
    setEnviandoUnico('video');
    try {
      const preparado = await prepararVideoOS(videoArquivo);
      const fd = new FormData();
      fd.append('ordem_servico_id', String(id));
      fd.append('tipo', tipoImagem);
      if (podeDescricaoImagem && descricaoVideo) fd.append('descricao', descricaoVideo);
      fd.append('imagem', preparado);
      const resp = await api<{ id: number; imagem_url: string; arquivo_tipo?: string | null; descricao?: string | null }>(
        '/os/os_imagens.php', { method: 'POST', body: fd, isForm: true },
      );
      const enviada: ImagemOS = { id: resp.id, imagem_url: resp.imagem_url, tipo: tipoImagem, created_at: new Date().toISOString(), arquivo_tipo: resp.arquivo_tipo, descricao: resp.descricao ?? null };
      setOs((atual) => atual ? ({ ...atual, imagens: [...(atual.imagens || []), enviada] }) : atual);
      setVideoArquivo(null);
      setDescricaoVideo('');
      setMsg('Vídeo anexado.');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviandoUnico(null);
    }
  }

  async function selecionarVideo(file?: File | null) {
    setErro('');
    setMsg('');
    if (!file) {
      setVideoArquivo(null);
      return;
    }
    setValidandoVideo(true);
    try {
      await validarVideoOS(file);
      setVideoArquivo(file);
    } catch (e) {
      setVideoArquivo(null);
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setValidandoVideo(false);
    }
  }

  // Retorna null se OK (checklist válido e salvo) ou a mensagem de erro (bloqueia).
  // Usado tanto pelo botão "Salvar checklist" quanto pela validação do "Próximo".
  async function salvarChecklist(): Promise<string | null> {
    if (!os) return null;
    setErro('');
    setMsg('');
    setSalvandoChecklist(true);
    try {
      const itens = checklistModelos.flatMap((m) => m.itens || []);
      if (!itens.length) return null; // sem itens cadastrados: nada obrigatório a preencher
      for (const item of itens) {
        const resp = checklistRespostas[item.id];
        if (marcado(item.obrigatorio) && !resp?.marcado) {
          throw new Error('Marque o item obrigatório: ' + item.descricao);
        }
        if (marcado(item.exige_observacao) && !(resp?.observacao || '').trim()) {
          throw new Error('Preencha a observação do item: ' + item.descricao);
        }
        if (marcado(item.exige_foto) && !resp?.imagem_url && !checklistArquivos[item.id]) {
          throw new Error('Anexe a foto do item: ' + item.descricao);
        }
      }
      const respostas = [];
      for (const item of itens) {
        let imagemUrl = checklistRespostas[item.id]?.imagem_url || '';
        const arquivoItem = checklistArquivos[item.id];
        if (arquivoItem) {
          const fd = new FormData();
          fd.append('imagem', await comprimirImagem(arquivoItem));
          const up = await api<{ imagem_url: string }>('/checklist/imagem.php', { method: 'POST', body: fd, isForm: true });
          imagemUrl = up.imagem_url;
        }
        respostas.push({
          checklist_item_id: item.id,
          marcado: Boolean(checklistRespostas[item.id]?.marcado),
          observacao: checklistRespostas[item.id]?.observacao || null,
          imagem_url: imagemUrl || null,
        });
      }
      await api('/checklist/respostas.php', {
        method: 'POST',
        body: { ordem_servico_id: os.id, respostas },
      });
      setMsg('Checklist salvo.');
      await carregar();
      return null;
    } catch (e2) {
      const m = e2 instanceof Error ? e2.message : String(e2);
      setErro(m);
      return m;
    } finally {
      setSalvandoChecklist(false);
    }
  }

  if (erro && !os) return <div className="msg-erro">{erro}</div>;
  if (!os) return <Loading />;

  // Permissao de empresa "LANCAR DADOS EM ORDENS DE SERVICO JA CONCLUIDA":
  // gestor da empresa e supervisor voltam a lancar dados numa OS CONCLUIDA.
  // Tecnico e solicitante continuam bloqueados, e OS CANCELADA segue travada
  // para todos. Espelha exatamente o que o backend faz em osEncerrada().
  const podeLancarEmOsConcluida = Boolean(user?.lancar_dados_os_concluida_habilitado)
    && ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const encerrada = os.status === 'cancelado'
    || (os.status === 'concluido' && !podeLancarEmOsConcluida);
  // Processo por etapas: a OS so e fechada pelo GESTOR e somente quando nao
  // resta etapa aplicavel pendente. Concluir uma etapa nunca conclui a OS.
  const etapasPendentes = etapasHabilitado ? etapas.filter((e) => e.status !== 'concluida') : [];
  // A etapa e POR TECNICO: o tecnico ve as 12 DELE (e nada do colega); o gestor
  // ve todas, agrupadas por tecnico. A sequencia obrigatoria vale dentro do
  // conjunto de cada um.
  const etapasVisiveis = ehGestorEtapas
    ? etapas
    : etapas.filter((e) => e.usuario_id === Number(user?.id));
  const gruposEtapas: { usuarioId: number | null; nome: string | null; itens: EtapaOS[] }[] = [];
  etapasVisiveis.forEach((e) => {
    const grupo = gruposEtapas.find((g) => g.usuarioId === e.usuario_id);
    if (grupo) grupo.itens.push(e);
    else gruposEtapas.push({ usuarioId: e.usuario_id, nome: e.tecnico_nome, itens: [e] });
  });
  const bloqueioConclusaoEtapas = !etapasHabilitado || encerrada
    ? null
    : !etapasCarregadas
      ? 'Carregando as etapas da OS...'
      : !ehGestorEtapas
        ? 'Somente o gestor pode concluir a OS quando o processo por etapas está ativo.'
        : etapasPendentes.length
          ? 'Ainda há etapas pendentes: ' + etapasPendentes.map((e) => e.etapa_nome).join('; ') + '.'
          : null;
  const etapasAguardandoGestor = etapasHabilitado && !encerrada && Boolean(etapasResumo?.aguardando_fechamento_gestor);
  const meuResumoEtapas = etapasResumo?.por_tecnico.find((t) => t.usuario_id === Number(user?.id)) || null;
  const podeAgendarBase = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');
  const podeAgendar = podeAgendarBase && (
    !user?.somente_gestor_supervisor_pode_reagendar_habilitado || podeAgendarBase
  );
  const podeReagendarStatus = user?.somente_gestor_supervisor_pode_reagendar_habilitado
    ? os.status !== 'concluido'
    : !encerrada;
  const podeEditarInicioManual = user?.perfil !== 'tecnico' || Boolean(user?.pode_editar_inicio_os);
  const podeEditarObsConclusaoEncerrada = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '')
    || (user?.perfil === 'tecnico' && Boolean(user?.pode_editar_obs_conclusao_os));
  const podeEditarInicioAgora = podeEditarInicioManual && os.status === 'em_andamento' && !encerrada;
  const podeEditarTermino = podeEditarInicioManual && os.status === 'em_andamento' && !encerrada;
  const dataExecucao = (sp?: string | null, raw?: string | null) => sp || dataHora(raw);
  const tempoTotalMinutos = os.tempo_total_calculado_minutos ?? os.tempo_total_minutos ?? 0;
  const custoMateriais = podeVerValoresMonetarios
    ? (os.materiais_total ?? os.materiais.reduce((s, m) => s + Number(m.valor_total || 0), 0))
    : null;
  const custoMaoDeObra = podeVerValoresMonetarios && !ocultarCustoMaoDeObra && os.mao_obra_valor !== null && os.mao_obra_valor !== undefined
    ? Number(os.mao_obra_valor)
    : null;
  const valorHoraTecnico = podeVerValoresMonetarios && !ocultarCustoMaoDeObra && os.tecnico_valor_hora !== null && os.tecnico_valor_hora !== undefined
    ? Number(os.tecnico_valor_hora)
    : null;
  const custoTotal = podeVerValoresMonetarios && !ocultarCustoMaoDeObra
    ? (os.custo_total ?? ((custoMateriais || 0) + (custoMaoDeObra || 0)))
    : null;
  const orcamentoAtual = os.orcamento_valor === null || os.orcamento_valor === undefined || os.orcamento_valor === '' ? null : Number(os.orcamento_valor);
  const historicoOrcamento = os.orcamento_historico || [];
  const modoOficina = user?.modo_visualizacao === 'oficina';
  const mostrarInfoEquipamentoNaAbaMateriais = dadosEquipamentoHabilitado && Boolean(os.ativo_id);
  const capacidadeEquipamento = [os.ativo_modelo, os.ativo_marca].filter((item) => String(item || '').trim()).join(' ');
  const patrimonioEquipamento = (os.ativo_patrimonio || '').trim();
  const veiculoTexto = [os.veiculo_placa, os.veiculo_marca, os.veiculo_modelo, os.veiculo_ano].filter(Boolean).join(' ');
  const voltarOs = '/app/os';
  const qtdAnexos = arquivos.length + (videoArquivo ? 1 : 0);
  const MAX_VIDEOS_POR_OS = 2;
  const qtdVideosDaOs = os.imagens.filter((img) => img.arquivo_tipo === 'video' || urlEhVideo(img.imagem_url)).length;
  const limiteVideosAtingido = qtdVideosDaOs + (videoArquivo ? 1 : 0) >= MAX_VIDEOS_POR_OS;
  const itensChecklist = checklistModelos.flatMap((modelo) => modelo.itens || []);
  const todosChecklistMarcados = itensChecklist.length > 0 && itensChecklist.every((item) => Boolean(checklistRespostas[item.id]?.marcado));
  const checklistNode = (
    <div className="card">
      <h3>Checklist da OS</h3>
      {!encerrada && !!itensChecklist.some((it) => marcado(it.obrigatorio) || marcado(it.exige_observacao) || marcado(it.exige_foto)) && (
        <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>Itens obrigatórios (*) precisam ser preenchidos antes de avançar.</p>
      )}
      {!checklistModelos.length && (
        <p style={{ color: 'var(--texto-suave)' }}>Nenhum checklist vinculado ao tipo de chamado ou ao equipamento desta OS. Cadastre um modelo de checklist para a categoria correspondente.</p>
      )}
      {!!checklistModelos.length && (
        <form onSubmit={(e) => { e.preventDefault(); salvarChecklist(); }}>
          {!encerrada && !!itensChecklist.length && (
            <div className="checklist-os-item" style={{ marginTop: 10 }}>
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={todosChecklistMarcados}
                  onChange={(ev) => marcarTodosChecklist(ev.target.checked)}
                />
                <span>Selecionar todos</span>
              </label>
            </div>
          )}
          {checklistModelos.map((modelo) => (
            <div key={modelo.id} style={{ marginTop: 10 }}>
              <strong>{modelo.nome}</strong>
              <div className="checklist-os-list">
                {modelo.itens.map((item) => (
                  <div className="checklist-os-item" key={item.id}>
                    <label className="check-line">
                      <input
                        type="checkbox"
                        checked={Boolean(checklistRespostas[item.id]?.marcado)}
                        onChange={(ev) => setResposta(item.id, { marcado: ev.target.checked })}
                        disabled={encerrada}
                      />
                      <span>{item.descricao}{marcado(item.obrigatorio) ? ' *' : ''}</span>
                    </label>
                    <input
                      className="input"
                      value={checklistRespostas[item.id]?.observacao || ''}
                      onChange={(ev) => setResposta(item.id, { observacao: ev.target.value })}
                      placeholder={marcado(item.exige_observacao) ? 'Observação obrigatória' : 'Observação'}
                      disabled={encerrada}
                      readOnly={encerrada}
                    />
                    <div className="checklist-os-upload">
                      {!encerrada && (
                        <input
                          className="input mini-file"
                          type="file"
                          accept="image/*"
                          onChange={(ev) => setChecklistArquivos((atuais) => ({ ...atuais, [item.id]: ev.target.files?.[0] || null }))}
                        />
                      )}
                      {checklistRespostas[item.id]?.imagem_url && (
                        <a href={urlImagem(checklistRespostas[item.id].imagem_url)} target="_blank" rel="noreferrer">
                          <img src={urlImagem(checklistRespostas[item.id].imagem_url)} alt="" className="thumb" />
                        </a>
                      )}
                      {checklistArquivos[item.id] && <span className="badge" style={{ background: '#dcfce7', color: '#15803d' }}>✓ nova foto — salve o checklist</span>}
                      {marcado(item.exige_foto) && <span className="badge">foto exigida</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!encerrada && (
            <div className="form-actions">
              <button className="btn" disabled={salvandoChecklist}>{salvandoChecklist ? 'Salvando...' : 'Salvar checklist'}</button>
            </div>
          )}
        </form>
      )}
    </div>
  );

  const orcamentoNode = (
    <div className="card">
      <h3>Orcamento da OS</h3>
      <div className="grid cols2" style={{ gap: 10, alignItems: 'end' }}>
        <div>
          <label>Valor atual</label>
          <input
            className="input"
            inputMode="numeric"
            value={moedaMascara(orcamentoForm)}
            onChange={(e) => setOrcamentoForm(e.target.value.replace(/\D/g, ''))}
            placeholder="R$ 0,00"
            disabled={encerrada}
            readOnly={encerrada}
          />
        </div>
        <div className="os-campo">
          <span className="k">Orcamento salvo</span>
          <span className="v">{orcamentoAtual === null ? 'Nao informado' : moeda(orcamentoAtual)}</span>
        </div>
      </div>
      {!encerrada && (
        <div className="form-actions">
          <button type="button" className="btn" disabled={salvandoOrcamento} onClick={salvarOrcamento}>
            {salvandoOrcamento ? 'Salvando...' : 'Salvar orcamento'}
          </button>
        </div>
      )}
      <div className="tabela-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead><tr><th>Quando</th><th>Usuario</th><th>Valor anterior</th><th>Novo valor</th></tr></thead>
          <tbody>
            {historicoOrcamento.map((item) => (
              <tr key={item.id}>
                <td>{dataHora(item.created_at)}</td>
                <td>{item.usuario_nome || (item.usuario_id ? `#${item.usuario_id}` : '-')}</td>
                <td>{item.valor_anterior === null || item.valor_anterior === '' ? '-' : moeda(item.valor_anterior)}</td>
                <td>{item.valor_novo === null || item.valor_novo === '' ? '-' : moeda(item.valor_novo)}</td>
              </tr>
            ))}
            {!historicoOrcamento.length && <tr><td colSpan={4} className="vazio">Nenhuma alteracao registrada.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const materiaisNode = (
    <div className="card">
      <h3>Materiais usados</h3>
      {mostrarInfoEquipamentoNaAbaMateriais && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            border: '1px solid var(--linha)',
            borderRadius: 12,
            background: 'var(--fundo)',
          }}
        >
          <div><strong>Capacidade:</strong> {capacidadeEquipamento || '-'}</div>
          <div style={{ marginTop: 4 }}><strong>CA:</strong> {patrimonioEquipamento || '-'}</div>
        </div>
      )}

      {/* Servicos da OS (varios por OS) - permissao "CRIACAO DE OS SIMPLIFICADA"
          OU "SERVICOS" (com "SERVICOS" as opcoes da tabela de precos tambem
          filtram por capacidade em BTU do equipamento da OS). Ajustaveis aqui
          enquanto a OS nao esta encerrada: o tecnico descobre em campo que
          faltou um servico. */}
      {(osSimplificadaHabilitado || servicosHabilitado) && servicosOsLista && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            border: '1px solid var(--linha)',
            borderRadius: 12,
            background: 'var(--fundo)',
          }}
        >
          <label style={{ display: 'block', marginBottom: 6 }}>Serviços da OS</label>

          {/* Selecionar so PREENCHE o formulario (nunca salva sozinho) - so o
              clique em "Adicionar" grava. Com tabela de precos, o servico vem
              so de la (sem campo livre): o valor unitario ja chega preenchido
              do item escolhido, so a quantidade fica por conta de quem lanca.
              Sem tabela de precos (OS simplificada sem o modulo Servicos), o
              servico e digitado livremente, unico jeito que sempre funcionou. */}
          {!encerrada && (
            <div className="grid cols2" style={{ gap: 8, alignItems: 'end', marginBottom: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Serviço</label>
                {!!servicosOsLista.opcoes.length ? (
                  <select
                    value={novoServicoOs.servico_id}
                    disabled={salvandoServicosOs}
                    onChange={(e) => escolherServicoDaTabela(e.target.value)}
                  >
                    <option value="">Selecione o serviço</option>
                    {servicosOsLista.opcoes.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.rotulo}
                        {podeVerValoresMonetarios
                          ? (o.valor_unitario === null ? ' — sem preço' : ' — ' + moeda(o.valor_unitario))
                          : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      className="input"
                      list="sugestoes-servico-os-detalhe"
                      value={novoServicoOs.descricao}
                      disabled={salvandoServicosOs}
                      onChange={(e) => digitarDescricaoServicoOs(e.target.value)}
                      placeholder="Ex.: Limpeza completa de split 12.000 BTU"
                    />
                    <datalist id="sugestoes-servico-os-detalhe">
                      {servicosOsLista.sugestoes.map((s) => <option key={s.descricao} value={s.descricao} />)}
                    </datalist>
                  </>
                )}
              </div>
              {podeVerValoresMonetarios && (
                <div>
                  <label>Valor unitário</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={novoServicoOs.valor_unitario}
                    disabled={salvandoServicosOs}
                    onChange={(e) => setNovoServicoOs({ ...novoServicoOs, valor_unitario: e.target.value })}
                    placeholder="vazio = sem preço"
                  />
                </div>
              )}
              <div>
                <label>Quantidade</label>
                <input
                  className="input"
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={novoServicoOs.quantidade}
                  disabled={salvandoServicosOs}
                  onChange={(e) => setNovoServicoOs({ ...novoServicoOs, quantidade: e.target.value })}
                />
              </div>
              <div className="form-actions" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="btn"
                  disabled={salvandoServicosOs || (!!servicosOsLista.opcoes.length && !novoServicoOs.servico_id)}
                  onClick={adicionarServicoOs}
                >
                  {salvandoServicosOs ? 'Salvando...' : 'Adicionar'}
                </button>
              </div>
            </div>
          )}

          <div className="tabela-wrap">
            <table>
              <thead>
                <tr>
                  <th>Serviço</th>
                  <th style={{ width: 90 }}>Qtd</th>
                  <th style={{ width: 150 }}>Valor unit.</th>
                  <th style={{ width: 120 }}>Total</th>
                  {!encerrada && <th style={{ width: 90 }} />}
                </tr>
              </thead>
              <tbody>
                {servicosOsLista.itens.map((s) => (
                  <tr key={s.id}>
                    <td>{s.descricao}</td>
                    <td>{Number(s.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false })}</td>
                    <td>
                      {!podeVerValoresMonetarios
                        ? '-'
                        : s.valor_unitario === null
                          ? <span style={{ color: 'var(--texto-suave)', fontStyle: 'italic' }}>sem preço contratado</span>
                          : moeda(s.valor_unitario)}
                    </td>
                    <td>{!podeVerValoresMonetarios ? '-' : (s.valor_total === null ? '—' : moeda(s.valor_total))}</td>
                    {!encerrada && (
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn danger sm"
                          disabled={salvandoServicosOs}
                          onClick={() => salvarServicosOs(listaServicosParaEnvio(s.id), 'Serviço removido da OS.')}
                        >
                          Remover
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!servicosOsLista.itens.length && (
                  <tr><td colSpan={encerrada ? 4 : 5} className="vazio">Nenhum serviço lançado.</td></tr>
                )}
                <tr>
                  <th colSpan={3} style={{ textAlign: 'right' }}>Total de serviços</th>
                  <th>{podeVerValoresMonetarios && servicosOsLista.total !== null ? moeda(servicosOsLista.total) : '-'}</th>
                  {!encerrada && <th />}
                </tr>
              </tbody>
            </table>
          </div>

          {podeVerValoresMonetarios && servicosOsLista.sem_preco > 0 && (
            <small style={{ display: 'block', marginTop: 8, color: 'var(--texto-suave)' }}>
              {servicosOsLista.sem_preco === 1
                ? '1 serviço está sem preço e não entra no total.'
                : servicosOsLista.sem_preco + ' serviços estão sem preço e não entram no total.'}
            </small>
          )}

          {servicosHabilitado && servicosOsLista.filtro?.btus != null && (
            <small style={{ display: 'block', marginTop: 8, color: 'var(--texto-suave)' }}>
              {servicosOsLista.filtro.por_capacidade
                ? 'Opções filtradas por ' + (servicosOsLista.filtro.tipo_os || '') + ' + ' + servicosOsLista.filtro.btus.toLocaleString('pt-BR') + ' btus do equipamento.'
                : 'Capacidade ' + servicosOsLista.filtro.btus.toLocaleString('pt-BR') + ' btus não caiu em nenhuma faixa contratada — mostrando todos os serviços do tipo.'}
            </small>
          )}

          <small style={{ display: 'block', marginTop: 8, color: 'var(--texto-suave)' }}>
            Os serviços entram no Total geral abaixo, no documento impresso da OS e no relatório.
          </small>
        </div>
      )}
      {!encerrada && (
        <form onSubmit={adicionarMaterial} className="grid cols2" style={{ gap: 8, alignItems: 'end' }}>
          <div>
            <label>Material</label>
            <select value={matForm.material_id} onChange={(e) => {
              const v = e.target.value;
              const mat = materiaisCatalogo.find((m) => String(m.id) === v);
              setMatForm((f) => ({ ...f, material_id: v, valor_unitario: mat ? String(mat.valor_unitario ?? '') : f.valor_unitario }));
            }}>
              <option value="">Selecione</option>
              {materiaisCatalogo.map((m) => <option key={m.id} value={m.id}>{m.nome} (estoque: {Number(m.quantidade_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false })})</option>)}
              <option value="novo">+ Cadastrar novo material</option>
            </select>
          </div>
          {matForm.material_id === 'novo' && (
            <>
              <div>
                <label>Nome do novo material</label>
                <input className="input" value={matForm.novoNome} onChange={(e) => setMatForm({ ...matForm, novoNome: e.target.value })} placeholder="Ex.: Cabo de rede" />
              </div>
              <div>
                <label>Estoque inicial</label>
                <input className="input" type="number" step="0.001" min="0" value={matForm.novoEstoque} onChange={(e) => setMatForm({ ...matForm, novoEstoque: e.target.value })} placeholder="Qtd em estoque" />
              </div>
            </>
          )}
          <div>
            <label>Quantidade</label>
            <input className="input" type="number" step="0.001" min="0" value={matForm.quantidade} onChange={(e) => setMatForm({ ...matForm, quantidade: e.target.value })} />
          </div>
          <div>
            <label>Valor unitário (R$)</label>
            {podeVerValoresMonetarios && <input className="input" type="number" step="0.01" min="0" value={matForm.valor_unitario} onChange={(e) => setMatForm({ ...matForm, valor_unitario: e.target.value })} placeholder="opcional" />}
          </div>
          <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn" disabled={salvandoMaterial}>{salvandoMaterial ? 'Adicionando...' : 'Adicionar material à OS'}</button>
          </div>
        </form>
      )}
      <div className="tabela-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead><tr><th>Material</th><th>Qtd</th><th>Valor unit.</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {os.materiais.map((m) => (
              <tr key={m.id}>
                <td>{m.material_nome}</td>
                <td>{Number(m.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 3, useGrouping: false })}</td>
                <td>{podeVerValoresMonetarios ? `R$ ${Number(m.valor_unitario || 0).toFixed(2)}` : '-'}</td>
                <td>{podeVerValoresMonetarios ? `R$ ${Number(m.valor_total || 0).toFixed(2)}` : '-'}</td>
                <td style={{ textAlign: 'right' }}>
                  {!encerrada && (
                    <button
                      type="button"
                      className="btn danger sm"
                      disabled={removendoMaterial === m.id}
                      onClick={() => deletarMaterial(m.id)}
                    >
                      {removendoMaterial === m.id ? 'Excluindo...' : 'Excluir'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!os.materiais.length && <tr><td colSpan={5} className="vazio">Nenhum material adicionado.</td></tr>}
            <tr><th colSpan={4} style={{ textAlign: 'right' }}>Total materiais</th><th>{podeVerValoresMonetarios && custoMateriais !== null ? moeda(custoMateriais) : '-'}</th></tr>
            {!ocultarCustoMaoDeObra && <tr><th colSpan={4} style={{ textAlign: 'right' }}>Mao de obra</th><th>{podeVerValoresMonetarios && custoMaoDeObra !== null ? moeda(custoMaoDeObra) : '-'}</th></tr>}
            {(osSimplificadaHabilitado || servicosHabilitado) && !!os.os_servicos?.length && (
              <tr><th colSpan={4} style={{ textAlign: 'right' }}>Serviços</th><th>{podeVerValoresMonetarios && os.os_servicos_total !== null && os.os_servicos_total !== undefined ? moeda(os.os_servicos_total) : '-'}</th></tr>
            )}
            {!ocultarCustoMaoDeObra && <tr><th colSpan={4} style={{ textAlign: 'right' }}>Total geral</th><th>{podeVerValoresMonetarios && custoTotal !== null ? moeda(custoTotal) : '-'}</th></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const assinaturasNode = (
    <div className="card">
      <h3>Assinaturas</h3>
      {encerrada && <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>Assinaturas bloqueadas porque a OS está encerrada.</p>}
      <div className="grid cols2">
        <AssinaturaPad
          titulo="Assinatura do técnico"
          imagemUrl={urlImagem(os.assinatura_tecnico_url)}
          bloqueado={encerrada}
          onSalvar={(arquivo) => salvarAssinatura('tecnico', arquivo)}
        />
        <AssinaturaPad
          titulo="Assinatura do cliente"
          imagemUrl={urlImagem(os.assinatura_cliente_url)}
          bloqueado={encerrada}
          onSalvar={(arquivo) => salvarAssinatura('cliente', arquivo)}
        />
      </div>
    </div>
  );

  const observacaoNode = (
    <div className="card">
      <h3>Observação de conclusão</h3>
      {podeAdicionarObsConclusao ? (
        <>
          <textarea
            className="input"
            rows={4}
            value={obsConclusaoForm}
            onChange={(e) => setObsConclusaoForm(e.target.value)}
            placeholder="Informe a observação ao concluir a OS"
          />
          <div className="form-actions" style={{ marginTop: 8 }}>
            <button type="button" className="btn sec sm" disabled={salvandoObsConclusao} onClick={salvarObsConclusao}>
              {salvandoObsConclusao ? 'Salvando...' : 'Salvar observação'}
            </button>
          </div>
        </>
      ) : (
        <p className="vazio">{os.observacao_conclusao || 'Nenhuma observação registrada.'}</p>
      )}
    </div>
  );

  // Campo de tecnico do agendamento. Com o processo por etapas ativo a OS pode ir
  // para 1 ou MAIS tecnicos; sem a permissao segue o select de um tecnico so.
  // Com etapas ativo a OS pode ter varios tecnicos: mostra todos onde antes
  // aparecia apenas o principal (ordens_servico.tecnico_id).
  const textoTecnicosOs = etapasHabilitado && etapasTecnicosOs.length
    ? etapasTecnicosOs.map((t) => t.nome || ('#' + t.usuario_id)).join(', ')
    : (os.tecnico_nome || '-');

  const campoTecnicosAgenda = etapasHabilitado ? (
    <>
      <label>Técnicos responsáveis</label>
      <div style={{ display: 'grid', gap: 4, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--cinza-borda)', borderRadius: 10, padding: 8, background: '#fff' }}>
        {usuarios.map((u) => (
          <label key={u.id} className="check-line" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={ag.tecnico_ids.includes(u.id)}
              onChange={(e) =>
                setAg((atual) => ({
                  ...atual,
                  tecnico_ids: e.target.checked
                    ? [...atual.tecnico_ids, u.id]
                    : atual.tecnico_ids.filter((x) => x !== u.id),
                }))
              }
            />
            <span>{u.nome}{u.perfil ? ' (' + u.perfil + ')' : ''}</span>
          </label>
        ))}
        {!usuarios.length && <span className="vazio">Nenhum usuário disponível.</span>}
      </div>
      <p style={{ color: 'var(--texto-suave)', fontSize: 12, margin: '4px 0 0' }}>
        {ag.tecnico_ids.length
          ? `${ag.tecnico_ids.length} técnico(s) — todos executam as 12 etapas desta OS.`
          : 'Sem técnico: as etapas não ficam disponíveis para ninguém.'}
      </p>
    </>
  ) : (
    <>
      <label>Técnico responsável</label>
      <select value={ag.tecnico_id} onChange={(e) => setAg({ ...ag, tecnico_id: e.target.value })}>
        <option value="">Sem técnico definido</option>
        {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome}{u.perfil ? ' (' + u.perfil + ')' : ''}</option>)}
      </select>
    </>
  );

  const etapasNode = (
    <div className="card">
      <h3>Etapas do processo</h3>
      {!etapasCarregadas ? (
        <p className="vazio">Carregando etapas...</p>
      ) : (
        <>
          {/* A atribuição é da OS: o gestor define UM técnico em "Reagendar / trocar
              técnico". Feito isso, as 12 etapas ficam disponíveis para ele. */}
          <div style={{ marginBottom: 14, padding: 12, border: '1px solid var(--cinza-borda)', borderRadius: 12, background: 'var(--cinza)' }}>
            <strong style={{ display: 'block', marginBottom: 2 }}>Técnicos da OS</strong>
            {etapasTecnicosOs.length ? (
              <p style={{ margin: 0, fontSize: 14 }}>
                {etapasTecnicosOs.map((t) => t.nome || ('#' + t.usuario_id)).join(', ')}
                <span style={{ color: 'var(--texto-suave)', fontSize: 13 }}> — executam as 12 etapas, em ordem.</span>
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
                {ehGestorEtapas
                  ? 'Nenhum técnico. Use "Reagendar / trocar técnico" para agendar a OS para um ou mais técnicos — só então as etapas ficam disponíveis.'
                  : 'A OS ainda não foi agendada para nenhum técnico.'}
              </p>
            )}
          </div>
          <p style={{ color: 'var(--texto-suave)', margin: '0 0 12px', fontSize: 13, lineHeight: 1.5 }}>
            Cada técnico tem as suas 12 etapas, com imagem e checklist próprios — o trabalho de
            um não aparece no do outro. A etapa em destaque é a atual; a próxima libera quando ela
            for concluída. Concluir etapas não encerra a OS — o fechamento é do gestor.
          </p>
          {ehGestorEtapas && etapasResumo && etapasResumo.por_tecnico.length > 0 && (
            <div style={{ display: 'grid', gap: 4, margin: '0 0 12px' }}>
              {etapasResumo.por_tecnico.map((t) => (
                <div key={t.usuario_id ?? 'sem'} style={{ fontSize: 13 }}>
                  <strong>{t.nome || 'Sem técnico'}</strong>{': '}
                  {t.concluidas} de {t.total} concluídas
                  {t.todas_concluidas ? (
                    <span style={{ color: 'var(--ok)', fontWeight: 700 }}>
                      {' · concluiu a parte dele'}{t.finalizado_em_sp ? ` em ${t.finalizado_em_sp}` : ''}
                    </span>
                  ) : (
                    <span>{t.etapa_atual_nome ? ` · atual: ${t.etapa_atual_nome}` : ''}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {etapasAguardandoGestor && (
            <div className="msg-ok">
              Todas as etapas de todos os técnicos foram concluídas — a OS aguarda o fechamento do gestor.
            </div>
          )}
          {/* O técnico terminou as 12 dele: a participação dele acabou, mas a OS
              continua em execução até o gestor fechar. */}
          {!ehGestorEtapas && meuResumoEtapas?.todas_concluidas && !etapasAguardandoGestor && (
            <div className="msg-ok">
              Você concluiu todas as suas etapas
              {meuResumoEtapas.finalizado_em_sp ? ` em ${meuResumoEtapas.finalizado_em_sp}` : ''}.
              {' '}A OS segue <strong>em execução</strong> até o gestor fechá-la.
            </div>
          )}

          {!etapasVisiveis.length && (
            <p className="vazio">
              {ehGestorEtapas
                ? 'Nenhuma etapa ainda — agende a OS para um ou mais técnicos.'
                : 'Você não tem etapas nesta OS.'}
            </p>
          )}
          {gruposEtapas.map((grupo) => {
            // 4 etapas por pagina, para o tecnico nao rolar as 12 no celular.
            const chaveGrupo = grupo.usuarioId ?? 0;
            const totalPaginasEtapas = Math.max(1, Math.ceil(grupo.itens.length / ETAPAS_POR_PAGINA));
            const paginaAtual = Math.min(etapaPagina[chaveGrupo] ?? 0, totalPaginasEtapas - 1);
            const inicio = paginaAtual * ETAPAS_POR_PAGINA;
            const itensPagina = grupo.itens.slice(inicio, inicio + ETAPAS_POR_PAGINA);
            const irParaPagina = (p: number) =>
              setEtapaPagina((atuais) => ({ ...atuais, [chaveGrupo]: Math.max(0, Math.min(totalPaginasEtapas - 1, p)) }));
            return (
          <div key={grupo.usuarioId ?? 'sem'} style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {ehGestorEtapas && (
              <div style={{ fontWeight: 800, color: '#0f172a', borderBottom: '2px solid var(--cinza-borda)', paddingBottom: 4 }}>
                {grupo.nome || 'Sem técnico'}
              </div>
            )}
            {itensPagina.map((etapa) => {
              const ocupada = etapaOcupada === etapa.id;
              // A etapa atual ja vem aberta, pronta para trabalhar; as demais o
              // usuario abre quando quiser consultar.
              const aberta = etapaAberta !== null ? etapaAberta === etapa.id : etapa.atual;
              // Executar e exclusivo do DONO da etapa (o gestor tambem, para destravar).
              const souDonoDaEtapa = etapa.usuario_id === Number(user?.id);
              const podeExecutar = (souDonoDaEtapa || ehGestorEtapas) && etapa.atual && !encerrada;
              const modelo = etapasModelos.find((m) => m.id === etapa.checklist_modelo_id);
              const rotuloEtapa = etapa.status === 'concluida'
                ? 'Concluída'
                : etapa.status === 'em_andamento'
                  ? 'Em andamento'
                  : etapa.atual ? 'Etapa atual' : 'Aguardando';
              const corEtapa = etapa.status === 'concluida'
                ? 'var(--ok)'
                : etapa.status === 'em_andamento'
                  ? '#b45309'
                  : etapa.atual ? '#1E66F5' : '#94a3b8';
              return (
                <div
                  key={etapa.id}
                  style={{
                    border: etapa.atual ? '2px solid #1E66F5' : '1px solid var(--cinza-borda)',
                    borderRadius: 12,
                    padding: 12,
                    background: etapa.status === 'concluida' ? '#f8fafc' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                      <strong style={{ display: 'block' }}>{etapa.ordem}. {etapa.etapa_nome}</strong>
                      <span style={{ color: corEtapa, fontSize: 13, fontWeight: 700 }}>{rotuloEtapa}</span>
                      {etapa.checklist_modelo_nome && (
                        <span style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                          {' · checklist: '}{etapa.checklist_modelo_nome}
                        </span>
                      )}
                      {etapa.bloqueada_por && etapa.status !== 'concluida' && (
                        <div style={{ color: '#b45309', fontSize: 13, marginTop: 4 }}>
                          Aguardando a etapa &ldquo;{etapa.bloqueada_por.etapa_nome}&rdquo; ser concluída.
                        </div>
                      )}
                      {(etapa.iniciado_em_sp || etapa.concluido_em_sp) && (
                        <div style={{ color: 'var(--texto-suave)', fontSize: 12, marginTop: 4 }}>
                          {etapa.iniciado_em_sp ? 'Início: ' + etapa.iniciado_em_sp : ''}
                          {etapa.iniciado_por_nome ? ' · por ' + etapa.iniciado_por_nome : ''}
                          {etapa.concluido_em_sp ? ' · Conclusão: ' + etapa.concluido_em_sp : ''}
                          {etapa.concluido_por_nome ? ' · por ' + etapa.concluido_por_nome : ''}
                        </div>
                      )}
                    </div>
                    <button type="button" className="btn sec sm" onClick={() => setEtapaAberta(aberta ? -1 : etapa.id)}>
                      {aberta ? 'Fechar' : 'Abrir'}
                    </button>
                  </div>

                  {aberta && (
                    <div style={{ marginTop: 12, borderTop: '1px solid var(--cinza-borda)', paddingTop: 12 }}>
                      {ehGestorEtapas && !encerrada && etapa.status !== 'concluida' && (
                        <div style={{ marginBottom: 14 }}>
                          <label>Checklist vinculado</label>
                          <select
                            value={etapa.checklist_modelo_id ? String(etapa.checklist_modelo_id) : ''}
                            disabled={ocupada}
                            onChange={(ev) => acaoEtapa(etapa, 'configurar', { checklist_modelo_id: ev.target.value ? Number(ev.target.value) : null })}
                          >
                            <option value="">Sem checklist</option>
                            {etapasModelos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                          </select>
                          {!etapasModelos.length && (
                            <p style={{ color: 'var(--texto-suave)', fontSize: 12, margin: '4px 0 0' }}>
                              Nenhum modelo de checklist cadastrado — crie em Cadastros &gt; Checklist.
                            </p>
                          )}
                        </div>
                      )}

                      {etapa.imagem_url && (
                        <div style={{ marginBottom: 10 }}>
                          <a href={urlImagem(etapa.imagem_url)} target="_blank" rel="noreferrer">
                            <img src={urlImagem(etapa.imagem_url)} alt="" style={{ maxWidth: 220, borderRadius: 10, border: '1px solid var(--cinza-borda)' }} />
                          </a>
                        </div>
                      )}
                      {podeExecutar && etapa.status !== 'concluida' && (
                        <div style={{ marginBottom: 12 }}>
                          <label>Imagem da etapa</label>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={ocupada}
                            onChange={(ev) => { const f = ev.target.files?.[0]; if (f) enviarImagemEtapa(etapa, f); ev.target.value = ''; }}
                          />
                        </div>
                      )}

                      {modelo && (modelo.itens || []).length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <label>Checklist — {modelo.nome}</label>
                          {(() => {
                            const itemIds = (modelo.itens || []).map((item) => item.id);
                            const todosMarcados = itemIds.length > 0
                              && itemIds.every((itemId) => Boolean(etapaChecklist[etapa.id]?.[itemId]?.marcado));
                            const travado = !podeExecutar || etapa.status !== 'em_andamento';
                            if (travado) return null;
                            return (
                              <div className="checklist-os-item" style={{ margin: '8px 0 10px' }}>
                                <label className="check-line">
                                  <input
                                    type="checkbox"
                                    checked={todosMarcados}
                                    disabled={ocupada}
                                    onChange={(ev) => marcarTodosChecklistEtapa(etapa.id, itemIds, ev.target.checked)}
                                  />
                                  <span>Selecionar todos</span>
                                </label>
                              </div>
                            );
                          })()}
                          {(modelo.itens || []).map((item) => {
                            const resp = etapaChecklist[etapa.id]?.[item.id];
                            const travado = !podeExecutar || etapa.status !== 'em_andamento';
                            return (
                              <div key={item.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--cinza-borda)' }}>
                                <label className="check-line" style={{ gap: 8, alignItems: 'flex-start' }}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(resp?.marcado)}
                                    disabled={travado || ocupada}
                                    onChange={(ev) => setRespostaEtapa(etapa.id, item.id, { marcado: ev.target.checked })}
                                  />
                                  <span>
                                    {item.descricao}
                                    {marcado(item.obrigatorio) && <strong style={{ color: 'var(--erro)' }}> *</strong>}
                                  </span>
                                </label>
                                {marcado(item.exige_observacao) && (
                                  <input
                                    className="input"
                                    style={{ marginTop: 6 }}
                                    placeholder="Observação"
                                    value={resp?.observacao || ''}
                                    disabled={travado || ocupada}
                                    onChange={(ev) => setRespostaEtapa(etapa.id, item.id, { observacao: ev.target.value })}
                                  />
                                )}
                                {/* Foto do item: sobe ao salvar o checklist e entra no relatório. */}
                                {marcado(item.exige_foto) && (
                                  <div style={{ marginTop: 6 }}>
                                    {resp?.imagem_url && (
                                      <a href={urlImagem(resp.imagem_url)} target="_blank" rel="noreferrer">
                                        <img
                                          src={urlImagem(resp.imagem_url)}
                                          alt=""
                                          style={{ maxWidth: 160, display: 'block', marginBottom: 4, borderRadius: 8, border: '1px solid var(--cinza-borda)' }}
                                        />
                                      </a>
                                    )}
                                    {!travado && (
                                      <input
                                        type="file"
                                        accept="image/*"
                                        disabled={ocupada}
                                        onChange={(ev) =>
                                          setEtapaChecklistArquivos((atuais) => ({
                                            ...atuais,
                                            [etapa.id + ':' + item.id]: ev.target.files?.[0] || null,
                                          }))
                                        }
                                      />
                                    )}
                                    {etapaChecklistArquivos[etapa.id + ':' + item.id] && (
                                      <small style={{ color: 'var(--texto-suave)' }}>
                                        Foto selecionada — clique em “Salvar checklist da etapa”.
                                      </small>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {podeExecutar && etapa.status === 'em_andamento' && (
                            <button type="button" className="btn sec sm" style={{ marginTop: 8 }} disabled={ocupada} onClick={() => salvarChecklistEtapa(etapa)}>
                              {ocupada ? 'Salvando...' : 'Salvar checklist da etapa'}
                            </button>
                          )}
                        </div>
                      )}

                      {podeExecutar && etapa.status !== 'concluida' && (
                        <div style={{ marginBottom: 10 }}>
                          <label>Observação da etapa</label>
                          <textarea
                            className="input"
                            rows={2}
                            value={etapaObsForm[etapa.id] || ''}
                            disabled={ocupada}
                            onChange={(ev) => setEtapaObsForm((atuais) => ({ ...atuais, [etapa.id]: ev.target.value }))}
                          />
                          {/* Salva sem concluir: o texto não se perde ao sair da tela. */}
                          <button
                            type="button"
                            className="btn sec sm"
                            style={{ marginTop: 6 }}
                            disabled={ocupada}
                            onClick={() => acaoEtapa(etapa, 'observacao', { observacao: etapaObsForm[etapa.id] || '' })}
                          >
                            {ocupada ? 'Salvando...' : 'Salvar observação'}
                          </button>
                        </div>
                      )}
                      {etapa.observacao && (
                        <p style={{ color: 'var(--texto-suave)', fontSize: 13, margin: '0 0 10px' }}>
                          <strong>Observação salva:</strong> {etapa.observacao}
                        </p>
                      )}
                      {!podeExecutar && etapa.atual && !encerrada && (
                        <p style={{ color: 'var(--texto-suave)', fontSize: 13, margin: 0 }}>
                          {etapa.usuario_id
                            ? 'Esta etapa é de ' + (etapa.tecnico_nome || 'outro técnico') + ' — somente consulta.'
                            : 'O gestor ainda não agendou esta OS para nenhum técnico.'}
                        </p>
                      )}
                      {podeExecutar && (
                        <div className="form-actions" style={{ marginTop: 4 }}>
                          {etapa.status === 'pendente' && (
                            <button type="button" className="btn sm" disabled={ocupada} onClick={() => acaoEtapa(etapa, 'iniciar')}>
                              {ocupada ? 'Aguarde...' : 'Iniciar etapa'}
                            </button>
                          )}
                          {etapa.status === 'em_andamento' && (
                            <button
                              type="button"
                              className="btn sm"
                              disabled={ocupada}
                              onClick={() => acaoEtapa(etapa, 'concluir', { observacao: etapaObsForm[etapa.id] || '' })}
                            >
                              {ocupada ? 'Aguarde...' : 'Concluir etapa'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {totalPaginasEtapas > 1 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                  Etapas {inicio + 1}–{Math.min(inicio + ETAPAS_POR_PAGINA, grupo.itens.length)} de {grupo.itens.length}
                  {' '}(página {paginaAtual + 1} de {totalPaginasEtapas})
                </span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn sec sm" disabled={paginaAtual <= 0} onClick={() => irParaPagina(paginaAtual - 1)}>
                    Anteriores
                  </button>
                  <button type="button" className="btn sec sm" disabled={paginaAtual >= totalPaginasEtapas - 1} onClick={() => irParaPagina(paginaAtual + 1)}>
                    Próximas
                  </button>
                </div>
              </div>
            )}
          </div>
            );
          })}
        </>
      )}
    </div>
  );

  const finalizacaoNode = (
    <div className="card">
      <h3>Finalização</h3>
      {novoFluxoAssinatura && (
        <div className="grid cols2" style={{ marginBottom: 14 }}>
          <AssinaturaPad
            titulo="Assinatura do técnico"
            imagemUrl={urlImagem(os.assinatura_tecnico_url)}
            bloqueado={encerrada}
            onSalvar={(arquivo) => salvarAssinatura('tecnico', arquivo)}
          />
          <AssinaturaPad
            titulo="Assinatura do cliente"
            imagemUrl={urlImagem(os.assinatura_cliente_url)}
            bloqueado={encerrada}
            onSalvar={(arquivo) => salvarAssinatura('cliente', arquivo)}
          />
        </div>
      )}
      <div className="grid cols2" style={{ marginTop: 8 }}>
        <div>
          <label>Inicio do atendimento</label>
          <input
            className="input"
            type="datetime-local"
            value={execForm.inicio_atendimento}
            onChange={(e) => setExecForm({ ...execForm, inicio_atendimento: e.target.value })}
            readOnly={!podeEditarInicioAgora}
            disabled={!podeEditarInicioAgora || !os.inicio_atendimento}
          />
        </div>
        <div>
          <label>Termino do atendimento</label>
          <input
            className="input"
            type="datetime-local"
            value={execForm.fim_atendimento}
            onChange={(e) => setExecForm({ ...execForm, fim_atendimento: e.target.value })}
            disabled={!podeEditarTermino}
          />
        </div>
      </div>
      {user?.perfil === 'tecnico' && !podeEditarInicioManual && (
        <p style={{ color: 'var(--texto-suave)', margin: '6px 0 0', fontSize: 13 }}>Inicio e termino registrados automaticamente pelo sistema (hora de Sao Paulo).</p>
      )}
      {!novoFluxoAssinatura && podeAdicionarObsConclusao && (
        <div style={{ marginTop: 12 }}>
          <label>Observação</label>
          <textarea
            className="input"
            rows={3}
            value={obsConclusaoForm}
            onChange={(e) => setObsConclusaoForm(e.target.value)}
            placeholder="Informe a observação ao concluir a OS"
          />
          <div className="form-actions" style={{ marginTop: 8 }}>
            <button type="button" className="btn sec sm" disabled={salvandoObsConclusao} onClick={salvarObsConclusao}>
              {salvandoObsConclusao ? 'Salvando...' : 'Salvar observação'}
            </button>
          </div>
        </div>
      )}
      <label>Estado da OS</label>
      <div className="estado-os-grid">
        {ESTADOS_VISIVEIS.map((estado) => {
          const atual = os.status === estado;
          const habilitado = podeMudarEstado(os.status, estado)
            && !(estado === 'concluido' && bloqueioConclusaoEtapas !== null);
          return (
            <button
              key={estado}
              type="button"
              className={'estado-os-btn st-' + estado + (atual ? ' atual' : '')}
              disabled={!habilitado}
              onClick={() => acaoEstado(estado)}
            >
              {rotuloBotaoEstado(os.status, estado)}
            </button>
          );
        })}
      </div>
      {bloqueioConclusaoEtapas && (
        <p style={{ color: '#b45309', fontSize: 13, margin: '8px 0 0' }}>{bloqueioConclusaoEtapas}</p>
      )}
      {etapasHabilitado && ehGestorEtapas && !bloqueioConclusaoEtapas && (
        <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--cinza-borda)', borderRadius: 12, background: 'var(--cinza)' }}>
          <strong style={{ display: 'block', marginBottom: 4 }}>Enviar encerramento ao cliente (e-mail)</strong>
          <p style={{ color: 'var(--texto-suave)', fontSize: 13, margin: '0 0 8px' }}>
            Envia o resumo da OS, o histórico das etapas por técnico e o link do documento completo (etapas, imagens e assinaturas).
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 240, flex: 1 }}>
              <label>Cliente</label>
              <select value={clienteSel} onChange={(e) => associarCliente(e.target.value)}>
                <option value="">Selecione o cliente</option>
                {clientesOpcoes.map((c) => (
                  <option key={c.id} value={c.id} disabled={Boolean(c.inativo)}>
                    {c.nome}{c.email ? ' · ' + c.email : ' · sem e-mail'}{c.inativo ? ' (desativado)' : ''}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                <button type="button" className="btn sec sm" onClick={() => setMostrarNovoCli((v) => !v)}>
                  + Novo cliente
                </button>
              </div>
              {mostrarNovoCli && (
                <div className="grid cols2" style={{ marginTop: 8, gap: 8, alignItems: 'end' }}>
                  <div>
                    <label>Nome do cliente</label>
                    <input className="input" value={novoCli.nome} onChange={(e) => setNovoCli({ ...novoCli, nome: e.target.value })} />
                  </div>
                  <div>
                    <label>E-mail</label>
                    <input className="input" type="email" value={novoCli.email} onChange={(e) => setNovoCli({ ...novoCli, email: e.target.value })} />
                  </div>
                  <div>
                    <label>WhatsApp (opcional)</label>
                    <input className="input" value={novoCli.whatsapp} onChange={(e) => setNovoCli({ ...novoCli, whatsapp: e.target.value })} />
                  </div>
                  <div className="form-actions" style={{ margin: 0 }}>
                    <button type="button" className="btn sm" disabled={salvandoCli} onClick={cadastrarClienteRapido}>
                      {salvandoCli ? 'Salvando...' : 'Cadastrar e vincular'}
                    </button>
                  </div>
                </div>
              )}
              <label style={{ marginTop: 10 }}>E-mail de envio</label>
              <input
                className="input"
                type="email"
                placeholder={os.cliente?.email ? '' : 'Cliente sem e-mail cadastrado — informe um'}
                value={emailClienteForm}
                onChange={(e) => setEmailClienteForm(e.target.value)}
              />
              {os.cliente?.email && emailClienteForm !== os.cliente.email && (
                <small style={{ color: 'var(--texto-suave)' }}>
                  Cadastro: {os.cliente.email}
                </small>
              )}
            </div>
            <button type="button" className="btn sec sm" disabled={enviandoEmailCliente} onClick={enviarEmailCliente}>
              {enviandoEmailCliente ? 'Enviando...' : 'Enviar e-mail'}
            </button>
          </div>
        </div>
      )}
      {podeAgendar && podeReagendarStatus && (
        <button type="button" className="btn sec sm" style={{ marginTop: 8 }} onClick={abrirAgendar}>📅 Reagendar / trocar técnico</button>
      )}
      {agendarAberto && (
        <form onSubmit={salvarAgendamento} style={{ marginTop: 10, padding: 14, border: '1px solid var(--cinza-borda)', borderRadius: 12, background: 'var(--cinza)' }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>Agendar chamado</strong>
          <div className="grid cols2">
            <div><label>Data</label><input className="input" type="date" value={ag.data_agendada} onChange={(e) => setAg({ ...ag, data_agendada: e.target.value })} /></div>
            <div><label>Hora</label><input className="input" type="time" value={ag.hora_agendada} onChange={(e) => setAg({ ...ag, hora_agendada: e.target.value })} /></div>
          </div>
          {campoTecnicosAgenda}
          <div className="form-actions">
            <button type="button" className="btn sec sm" onClick={() => setAgendarAberto(false)}>Cancelar</button>
            <button className="btn sm" disabled={salvandoAgenda}>{salvandoAgenda ? 'Salvando...' : 'Salvar agendamento'}</button>
          </div>
        </form>
      )}
      {podeExibirEnvioWhatsapp && (
        <>
      <label style={{ marginTop: 14 }}>Enviar ao cliente (WhatsApp)</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <select value={clienteSel} onChange={(e) => associarCliente(e.target.value)}>
            <option value="">Selecione o cliente</option>
            {clientesOpcoes.map((c) => (
              <option key={c.id} value={c.id} disabled={Boolean(c.inativo)}>
                {c.nome}{c.whatsapp ? ' · ' + c.whatsapp : ''}{c.inativo ? ' (desativado)' : ''}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="btn sec sm" onClick={() => setMostrarNovoCli((v) => !v)}>+ Novo cliente</button>
      </div>
      {mostrarNovoCli && (
        <div className="grid cols2" style={{ marginTop: 8, gap: 8, alignItems: 'end' }}>
          <div><label>Nome</label><input className="input" value={novoCli.nome} onChange={(e) => setNovoCli({ ...novoCli, nome: e.target.value })} /></div>
          <div><label>WhatsApp</label><input className="input" value={novoCli.whatsapp} onChange={(e) => setNovoCli({ ...novoCli, whatsapp: e.target.value })} placeholder="(11) 99999-9999" /></div>
          <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
            <button type="button" className="btn sm" disabled={salvandoCli} onClick={cadastrarClienteRapido}>{salvandoCli ? 'Salvando...' : 'Salvar e usar'}</button>
          </div>
        </div>
      )}
      {tecnicoAssinaturaWhatsappRestrita ? (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <button type="button" className="btn sm" disabled={!clienteWhats} onClick={enviarSomenteAssinatura}>Enviar p/ assinatura</button>
          </div>
          {!clienteWhats && <p style={{ color: 'var(--texto-suave)', fontSize: 13, marginTop: 6 }}>Selecione ou cadastre um cliente com WhatsApp para enviar.</p>}
          {clienteWhats && <p style={{ color: 'var(--texto-suave)', fontSize: 13, marginTop: 6 }}>O cliente recebe somente o link para assinar pelo proprio celular. Nesta permissao o PDF nao e enviado.</p>}
        </>
      ) : (
        <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        <button type="button" className="btn sm" disabled={!clienteWhats} onClick={enviarParaAssinatura}>✍️ Enviar p/ conferência e assinatura</button>
        <button type="button" className="btn sec sm" disabled={!clienteWhats} onClick={enviarPdfWhats}>📄 Enviar só o PDF</button>
      </div>
      {!clienteWhats && <p style={{ color: 'var(--texto-suave)', fontSize: 13, marginTop: 6 }}>Selecione ou cadastre um cliente com WhatsApp para enviar.</p>}
      {clienteWhats && <p style={{ color: 'var(--texto-suave)', fontSize: 13, marginTop: 6 }}>O cliente recebe o PDF para conferir e um link para assinar pelo próprio celular. A assinatura volta salva na OS.</p>}
        </>
      )}
        </>
      )}
      <label style={{ marginTop: 14 }}>Documento</label>
      <div><button type="button" className="btn sec sm" onClick={() => abrirDocumento()}>Abrir PDF/Impressão</button></div>
    </div>
  );

  const passosBase: Passo[] = [
    {
      titulo: 'Detalhes',
      node: (
        <>
          <div className="card">
            <h3>Dados</h3>
            <div className="os-dados">
              <div className="os-campo"><span className="k">Tipo</span><span className="v">{rotuloTipo(os.tipo_os)}</span></div>
              <div className="os-campo"><span className="k">Prioridade</span><span className="v"><span className={'badge pr-' + os.prioridade}>{rotuloPrioridade(os.prioridade)}</span></span></div>
              <div className="os-campo"><span className="k">Cliente</span><span className="v">{os.cliente?.nome || os.solicitante_nome || '-'}</span></div>
              {modoOficina && <div className="os-campo full"><span className="k">Veiculo</span><span className="v">{veiculoTexto || '-'}</span></div>}
              {!modoOficina && !semUnidadeLocalPiso && <div className="os-campo full"><span className="k">Local</span><span className="v">{[os.unidade_nome, os.piso_nome, os.local_nome].filter(Boolean).join(' / ') || '—'}</span></div>}
              {!modoOficina && isPremium && <div className="os-campo"><span className="k">{user?.rotulo_nome_ativo || 'Equipamento'}</span><span className="v">{os.ativo_nome || '-'}</span></div>}
              {!modoOficina && isPremium && !!os.ativo_numero_serie && <div className="os-campo"><span className="k">{user?.rotulo_numero_serie || 'Número de série'}</span><span className="v">{os.ativo_numero_serie}</span></div>}
              {mostrarInfoEquipamentoNaAbaMateriais && <div className="os-campo"><span className="k">Capacidade</span><span className="v">{capacidadeEquipamento || '-'}</span></div>}
              {mostrarInfoEquipamentoNaAbaMateriais && <div className="os-campo"><span className="k">CA</span><span className="v">{patrimonioEquipamento || '-'}</span></div>}
              <div className="os-campo"><span className="k">Solicitante</span><span className="v">{os.solicitante_nome || '-'}</span></div>
              <div className="os-campo"><span className="k">{etapasHabilitado && etapasTecnicosOs.length > 1 ? 'Técnicos' : 'Técnico'}</span><span className="v">{textoTecnicosOs}</span></div>
              <div className="os-campo"><span className="k">Agendamento</span><span className="v">{os.data_agendada ? dataHora(os.data_agendada + (os.hora_agendada ? ' ' + os.hora_agendada : '')) : 'Sem agendamento'}</span></div>
              <div className="os-campo"><span className="k">Inicio da execucao</span><span className="v">{dataExecucao(os.inicio_atendimento_sp, os.inicio_atendimento)}</span></div>
              <div className="os-campo"><span className="k">Termino da execucao</span><span className="v">{dataExecucao(os.fim_atendimento_sp, os.fim_atendimento)}</span></div>
              <div className="os-campo"><span className="k">Tempo total</span><span className="v">{tempoTotalMinutos ? `${tempoTotalMinutos} min` : '-'}</span></div>
              {podeVerValoresMonetarios && valorHoraTecnico !== null && <div className="os-campo"><span className="k">Valor/hora</span><span className="v">{moeda(valorHoraTecnico)}</span></div>}
              {podeVerValoresMonetarios && custoMaoDeObra !== null && <div className="os-campo"><span className="k">Mao de obra</span><span className="v">{moeda(custoMaoDeObra)}</span></div>}
              {podeVerValoresMonetarios && podeVerMateriaisOs && custoTotal !== null && <div className="os-campo"><span className="k">Custo total</span><span className="v">{moeda(custoTotal)}</span></div>}
              {podeUsarOrcamentoOs && <div className="os-campo"><span className="k">Orcamento</span><span className="v">{orcamentoAtual === null ? '-' : moeda(orcamentoAtual)}</span></div>}
              {os.avaria && <div className="os-campo full"><span className="k">Avaria</span><span className="v bloco">{os.avaria}</span></div>}
              {os.descricao && <div className="os-campo full"><span className="k">Descrição</span><span className="v bloco">{os.descricao}</span></div>}
              {os.observacao && <div className="os-campo full"><span className="k">Observação</span><span className="v bloco">{os.observacao}</span></div>}
              {os.observacao_conclusao && !(encerrada && podeEditarObsConclusaoEncerrada) && (
                <div className="os-campo full"><span className="k">Observação da conclusão</span><span className="v bloco">{os.observacao_conclusao}</span></div>
              )}
              {encerrada && podeEditarObsConclusaoEncerrada && (
                <div className="os-campo full">
                  <span className="k">Observação da conclusão</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={obsConclusaoForm}
                    onChange={(e) => setObsConclusaoForm(e.target.value)}
                    placeholder="Observação da conclusão"
                  />
                  <div className="form-actions">
                    <button type="button" className="btn sec sm" disabled={salvandoObsConclusao} onClick={salvarObsConclusao}>
                      {salvandoObsConclusao ? 'Salvando...' : 'Salvar observação da conclusão'}
                    </button>
                  </div>
                </div>
              )}
              {osSimplificadaHabilitado && (
                <div className="os-campo full">
                  <button type="button" className="btn sec sm" disabled={imprimindoOs} onClick={() => abrirDocumento(true)}>
                    {imprimindoOs ? 'Abrindo...' : 'Imprimir OS'}
                  </button>
                </div>
              )}
              {os.data_agendada && !encerrada && (
                <div className="os-campo full">
                  <label className="check-line">
                    <input
                      type="checkbox"
                      checked={marcado(os.lembrete_pausado ?? false)}
                      disabled={salvandoLembrete}
                      onChange={(e) => alternarLembrete(e.target.checked)}
                    />
                    Pausar lembrete automático desta OS
                  </label>
                </div>
              )}
            </div>
          </div>
          <div className="card" style={{ marginTop: 14 }}>
            <h3>Histórico</h3>
            <div className="tabela-wrap">
              <table>
                <thead><tr><th>Quando</th><th>Ação</th><th>De / Para</th></tr></thead>
                <tbody>
                  {os.historico.map((h) => (
                    <tr key={h.id}><td>{dataHora(h.created_at)}</td><td>{h.acao}</td>
                      <td>{h.status_anterior ? rotuloStatus(h.status_anterior) : '-'} / {h.status_novo ? rotuloStatus(h.status_novo) : '-'}</td></tr>
                  ))}
                  {!os.historico.length && <tr><td colSpan={3} className="vazio">Sem histórico.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ),
    },
    {
      titulo: 'Evidencias',
      node: (
        <div className="card">
          <h3>{isPremium ? 'Fotos e vídeos' : 'Fotos'}</h3>
          {!encerrada && (
            <form onSubmit={enviarImagem}>
              <div className="grid cols2">
                <div>
                  <label>Tipo</label>
                  <select value={tipoImagem} onChange={(e) => setTipoImagem(e.target.value)}>
                    <option value="abertura">Abertura</option>
                    <option value="execucao">Execução</option>
                    <option value="conclusao">Conclusão</option>
                  </select>
                </div>
                <div>
                  <label>Fotos</label>
                  <SeletorFotos
                    arquivos={arquivos}
                    onChange={(novos) => {
                      setDescricoesImagens((atual) => novos.map((f) => {
                        const idx = arquivos.indexOf(f);
                        return idx >= 0 ? (atual[idx] || '') : '';
                      }));
                      setArquivos(novos);
                    }}
                    max={5}
                    disabled={enviandoImagem || enviandoUnico !== null}
                  />
                  {podeDescricaoImagem && arquivos.length > 0 && (
                    <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                      {arquivos.map((arq, i) => (
                        <div key={arq.name + i} style={{ display: 'flex', gap: 4 }}>
                          <input
                            className="input"
                            placeholder={`Descrição da imagem ${i + 1} (opcional)`}
                            value={descricoesImagens[i] || ''}
                            disabled={enviandoImagem || enviandoUnico !== null}
                            onChange={(e) => setDescricoesImagens((atual) => {
                              const novo = [...atual];
                              novo[i] = e.target.value;
                              return novo;
                            })}
                          />
                          <button
                            type="button"
                            className="btn sec sm"
                            disabled={enviandoImagem || enviandoUnico !== null}
                            onClick={() => enviarFotoUnica(i)}
                          >
                            {enviandoUnico === i ? '...' : 'Salvar'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {isPremium ? (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Vídeo da execução</label>
                    <input
                      ref={videoGaleriaRef}
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      disabled={enviandoImagem}
                      onChange={(ev) => {
                        selecionarVideo(ev.currentTarget.files?.[0] || null);
                        ev.currentTarget.value = '';
                      }}
                    />
                    <input
                      ref={videoCameraRef}
                      type="file"
                      accept="video/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      disabled={enviandoImagem}
                      onChange={(ev) => {
                        selecionarVideo(ev.currentTarget.files?.[0] || null);
                        ev.currentTarget.value = '';
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" className="btn sec sm" disabled={enviandoImagem || enviandoUnico !== null || validandoVideo || limiteVideosAtingido} onClick={() => videoGaleriaRef.current?.click()}>
                        Selecionar vídeo
                      </button>
                      <button
                        type="button"
                        className="btn sec sm"
                        disabled={enviandoImagem || enviandoUnico !== null || validandoVideo || limiteVideosAtingido}
                        onClick={() => { if (suportaGravador()) setGravadorAberto(true); else videoCameraRef.current?.click(); }}
                      >
                        Gravar vídeo
                      </button>
                      {validandoVideo && <span className="badge">Validando vídeo...</span>}
                    </div>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                      Máximo de {VIDEO_OS_MAX_SEGUNDOS} segundos e 8 MB. Para clipes maiores, grave a câmera em 720p.
                      {' '}Limite de {MAX_VIDEOS_POR_OS} vídeos por OS ({qtdVideosDaOs}/{MAX_VIDEOS_POR_OS} já anexado{qtdVideosDaOs === 1 ? '' : 's'}).
                    </p>
                    {limiteVideosAtingido && (
                      <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--erro, #dc2626)' }}>
                        Limite de {MAX_VIDEOS_POR_OS} vídeos por OS atingido.
                      </p>
                    )}
                    {videoArquivo && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="badge">{videoArquivo.name}</span>
                        <button type="button" className="btn sec sm" disabled={enviandoImagem || enviandoUnico !== null} onClick={() => { setVideoArquivo(null); setDescricaoVideo(''); }}>Remover vídeo</button>
                      </div>
                    )}
                    {podeDescricaoImagem && videoArquivo && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                        <input
                          className="input"
                          placeholder="Descrição do vídeo (opcional)"
                          value={descricaoVideo}
                          disabled={enviandoImagem || enviandoUnico !== null}
                          onChange={(e) => setDescricaoVideo(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn sec sm"
                          disabled={enviandoImagem || enviandoUnico !== null}
                          onClick={enviarVideoUnico}
                        >
                          {enviandoUnico === 'video' ? '...' : 'Salvar'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Vídeo da execução</label>
                    <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                      🎬 Anexar vídeo da execução faz parte do plano Premium.
                    </p>
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button className="btn" disabled={!qtdAnexos || enviandoImagem || enviandoUnico !== null}>
                  {enviandoImagem ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          )}
          {(enviandoImagem || enviouConcluido) && createPortal(
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 3000,
                background: 'rgba(15, 23, 42, 0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: 380,
                  borderRadius: 16,
                  background: '#fff',
                  boxShadow: '0 30px 80px rgba(15, 23, 42, 0.35)',
                  padding: 24,
                  textAlign: 'center',
                }}
              >
                <strong style={{ display: 'block', fontSize: 16 }}>
                  {enviouConcluido ? 'Anexos salvos!' : 'Salvando anexos...'}
                </strong>
                <div
                  style={{
                    marginTop: 18,
                    height: 12,
                    borderRadius: 999,
                    background: 'var(--cinza, #f1f5f9)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progresso}%`,
                      borderRadius: 999,
                      background: enviouConcluido ? '#16a34a' : 'var(--azul, #1e66f5)',
                      transition: 'width .25s ease',
                    }}
                  />
                </div>
                <div style={{ marginTop: 10, fontSize: 22, fontWeight: 800, color: enviouConcluido ? '#16a34a' : 'var(--azul, #1e66f5)' }}>
                  {progresso}%
                </div>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  {enviouConcluido ? 'Envio concluído com sucesso.' : 'Não feche esta tela até concluir.'}
                </p>
                <button
                  type="button"
                  className="btn"
                  disabled={!enviouConcluido}
                  style={{ marginTop: 18, width: '100%' }}
                  onClick={() => { setEnviouConcluido(false); setProgresso(0); }}
                >
                  Continuar
                </button>
              </div>
            </div>,
            document.body,
          )}
          {gravadorAberto && (
            <GravadorVideo
              maxSegundos={VIDEO_OS_MAX_SEGUNDOS}
              onFechar={() => setGravadorAberto(false)}
              onGravado={(file) => { setVideoArquivo(file); setErro(''); setMsg(''); setGravadorAberto(false); }}
            />
          )}
          <div className="image-grid">
            {os.imagens.map((img) => (
              <div key={img.id} className="image-tile">
                {img.arquivo_tipo === 'video' || urlEhVideo(img.imagem_url) ? (
                  // #t=0.1 força o navegador a pintar o quadro de 0,1s como
                  // "poster" — sem isso o webm do MediaRecorder fica preto no tile.
                  <video src={urlImagem(img.imagem_url) + '#t=0.1'} controls muted playsInline preload="metadata" />
                ) : (
                  <a href={urlImagem(img.imagem_url)} target="_blank" rel="noreferrer">
                    <img src={urlImagem(img.imagem_url)} alt="" />
                  </a>
                )}
                <span>{img.tipo}</span>
                {!encerrada && <button type="button" className="tile-del" title="Excluir anexo" onClick={() => deletarImagem(img.id)}>×</button>}
                {podeDescricaoImagem && (
                  encerrada ? (
                    img.descricao && <small style={{ display: 'block', marginTop: 4, color: 'var(--texto-suave)' }}>{img.descricao}</small>
                  ) : (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      <input
                        className="input"
                        style={{ fontSize: 12, padding: '4px 8px' }}
                        placeholder="Descrição (opcional)"
                        value={edicaoDescricaoImg[img.id] ?? img.descricao ?? ''}
                        disabled={salvandoDescricaoImg === img.id}
                        onChange={(e) => setEdicaoDescricaoImg((atual) => ({ ...atual, [img.id]: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="btn sec sm"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        disabled={salvandoDescricaoImg === img.id || (edicaoDescricaoImg[img.id] ?? img.descricao ?? '') === (img.descricao ?? '')}
                        onClick={() => salvarDescricaoImagem(img.id, edicaoDescricaoImg[img.id] ?? '')}
                      >
                        {salvandoDescricaoImg === img.id ? '...' : 'Salvar'}
                      </button>
                    </div>
                  )
                )}
              </div>
            ))}
            {!os.imagens.length && <p className="vazio">Nenhuma evidencia anexada.</p>}
          </div>
        </div>
      ),
    },
    // Passo do processo por etapas: so entra no wizard com a permissao ativa.
    ...(etapasHabilitado ? [{ titulo: 'Etapas', node: etapasNode } as Passo] : []),
    ...(novoFluxoAssinatura
      ? [
          ...(podeUsarOrcamentoOs ? [{ titulo: 'Orcamento', node: orcamentoNode } as Passo] : []),
          ...(isPremium ? [{ titulo: 'Checklist', validar: encerrada ? undefined : salvarChecklist, node: checklistNode } as Passo] : []),
          ...(podeVerMateriaisOs ? [{ titulo: 'Materiais', node: materiaisNode } as Passo] : []),
          { titulo: 'Observação', node: observacaoNode } as Passo,
        ]
      : [
          ...(isPremium ? [{ titulo: 'Checklist', validar: encerrada ? undefined : salvarChecklist, node: checklistNode } as Passo] : []),
          ...(podeUsarOrcamentoOs ? [{ titulo: 'Orcamento', node: orcamentoNode } as Passo] : []),
          ...(podeVerMateriaisOs ? [{ titulo: 'Materiais', node: materiaisNode } as Passo] : []),
          { titulo: 'Assinaturas', node: assinaturasNode } as Passo,
        ]),
    { titulo: 'Finalização', node: finalizacaoNode },
  ];

  // Processo por etapas ATIVO: a execucao acontece dentro de cada etapa, entao as
  // abas tradicionais (Evidencias, Orcamento, Checklist, Materiais e Observacao/
  // Assinaturas) saem do wizard, e a Finalizacao fica so para gestor/supervisor.
  // "Detalhes" continua para todos: e a consulta dos dados e do historico da OS.
  // Com a permissao DESLIGADA o wizard segue exatamente como sempre foi.
  const passos: Passo[] = etapasHabilitado
    ? passosBase.filter((p) => p.titulo === 'Detalhes'
        || p.titulo === 'Etapas'
        || (p.titulo === 'Finalização' && ehGestorEtapas))
    : passosBase;

  return (
    <div>
      <div className="titulo-pg">
        <h1>OS {os.codigo} <span className={'badge st-' + os.status}>{rotuloStatus(os.status)}</span></h1>
        <Link to={voltarOs} className="btn sec sm">Voltar</Link>
      </div>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}
      {encerrada && (
        <div className="msg-encerrada">
          {os.status === 'cancelado' && podeAgendar && podeReagendarStatus
            ? `🔒 OS encerrada (${rotuloStatus(os.status)}) — somente consulta e PDF, exceto reagendamento e troca de técnico.`
            : `🔒 OS encerrada (${rotuloStatus(os.status)}) — somente consulta e PDF. Nenhuma ação pode ser alterada.`}
        </div>
      )}

      {os.status === 'aberto' ? (
        <div className="card">
          <h3>Chamado atribuído</h3>
          <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>
            Para executar este chamado é preciso <strong>iniciar o atendimento</strong>.{' '}
            {etapasHabilitado
              ? 'As etapas do processo só ficam disponíveis depois de iniciar.'
              : 'As etapas de execução (imagens, checklist, materiais, assinaturas e finalização) só ficam disponíveis depois de iniciar.'}
          </p>
          <div className="os-dados">
            <div className="os-campo"><span className="k">Tipo</span><span className="v">{rotuloTipo(os.tipo_os)}</span></div>
            <div className="os-campo"><span className="k">Prioridade</span><span className="v"><span className={'badge pr-' + os.prioridade}>{rotuloPrioridade(os.prioridade)}</span></span></div>
            <div className="os-campo"><span className="k">Cliente</span><span className="v">{os.cliente?.nome || os.solicitante_nome || '-'}</span></div>
            {modoOficina && <div className="os-campo full"><span className="k">Veiculo</span><span className="v">{veiculoTexto || '-'}</span></div>}
            {!modoOficina && !semUnidadeLocalPiso && <div className="os-campo full"><span className="k">Local</span><span className="v">{[os.unidade_nome, os.piso_nome, os.local_nome].filter(Boolean).join(' / ') || '—'}</span></div>}
            {!modoOficina && isPremium && <div className="os-campo"><span className="k">{user?.rotulo_nome_ativo || 'Equipamento'}</span><span className="v">{os.ativo_nome || '-'}</span></div>}
            {!modoOficina && isPremium && !!os.ativo_numero_serie && <div className="os-campo"><span className="k">{user?.rotulo_numero_serie || 'Número de série'}</span><span className="v">{os.ativo_numero_serie}</span></div>}
            {mostrarInfoEquipamentoNaAbaMateriais && <div className="os-campo"><span className="k">Capacidade</span><span className="v">{capacidadeEquipamento || '-'}</span></div>}
            {mostrarInfoEquipamentoNaAbaMateriais && <div className="os-campo"><span className="k">CA</span><span className="v">{patrimonioEquipamento || '-'}</span></div>}
            <div className="os-campo"><span className="k">{etapasHabilitado && etapasTecnicosOs.length > 1 ? 'Técnicos' : 'Técnico'}</span><span className="v">{textoTecnicosOs}</span></div>
            <div className="os-campo"><span className="k">Agendamento</span><span className="v">{os.data_agendada ? dataHora(os.data_agendada + (os.hora_agendada ? ' ' + os.hora_agendada : '')) : 'Sem agendamento'}</span></div>
            <div className="os-campo"><span className="k">Inicio da execucao</span><span className="v">{dataExecucao(os.inicio_atendimento_sp, os.inicio_atendimento)}</span></div>
            <div className="os-campo"><span className="k">Termino da execucao</span><span className="v">{dataExecucao(os.fim_atendimento_sp, os.fim_atendimento)}</span></div>
            {podeVerValoresMonetarios && valorHoraTecnico !== null && <div className="os-campo"><span className="k">Valor/hora</span><span className="v">{moeda(valorHoraTecnico)}</span></div>}
            {podeVerValoresMonetarios && custoMaoDeObra !== null && <div className="os-campo"><span className="k">Mao de obra</span><span className="v">{moeda(custoMaoDeObra)}</span></div>}
            {podeUsarOrcamentoOs && <div className="os-campo"><span className="k">Orcamento</span><span className="v">{orcamentoAtual === null ? '-' : moeda(orcamentoAtual)}</span></div>}
            {os.avaria && <div className="os-campo full"><span className="k">Avaria</span><span className="v bloco">{os.avaria}</span></div>}
            {os.descricao && <div className="os-campo full"><span className="k">Descrição</span><span className="v bloco">{os.descricao}</span></div>}
            {podeUsarOrcamentoOs && (
              <div className="os-campo full">
                <label>Orcamento da OS</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    className="input"
                    style={{ maxWidth: 220 }}
                    inputMode="numeric"
                    value={moedaMascara(orcamentoForm)}
                    onChange={(e) => setOrcamentoForm(e.target.value.replace(/\D/g, ''))}
                    placeholder="R$ 0,00"
                  />
                  <button type="button" className="btn sec sm" disabled={salvandoOrcamento} onClick={salvarOrcamento}>
                    {salvandoOrcamento ? 'Salvando...' : 'Salvar orcamento'}
                  </button>
                </div>
              </div>
            )}
            {os.data_agendada && !encerrada && (
              <div className="os-campo full">
                <label className="check-line">
                  <input
                    type="checkbox"
                    checked={marcado(os.lembrete_pausado ?? false)}
                    disabled={salvandoLembrete}
                    onChange={(e) => alternarLembrete(e.target.checked)}
                  />
                  Pausar lembrete automático desta OS
                </label>
              </div>
            )}
          </div>
          {agendarAberto && (
            <form onSubmit={salvarAgendamento} style={{ marginTop: 10, padding: 14, border: '1px solid var(--cinza-borda)', borderRadius: 12, background: 'var(--cinza)' }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Agendar chamado</strong>
              <div className="grid cols2">
                <div><label>Data</label><input className="input" type="date" value={ag.data_agendada} onChange={(e) => setAg({ ...ag, data_agendada: e.target.value })} /></div>
                <div><label>Hora</label><input className="input" type="time" value={ag.hora_agendada} onChange={(e) => setAg({ ...ag, hora_agendada: e.target.value })} /></div>
              </div>
              {campoTecnicosAgenda}
              <div className="form-actions">
                <button type="button" className="btn sec sm" onClick={() => setAgendarAberto(false)}>Cancelar</button>
                <button className="btn sm" disabled={salvandoAgenda}>{salvandoAgenda ? 'Salvando...' : 'Salvar agendamento'}</button>
              </div>
            </form>
          )}
          <div className="form-actions">
            {podeAgendar && podeReagendarStatus && !agendarAberto && <button type="button" className="btn sec" onClick={abrirAgendar}>📅 Reagendar / trocar técnico</button>}
            {osSimplificadaHabilitado && (
              <button type="button" className="btn sec" disabled={imprimindoOs} onClick={() => abrirDocumento(true)}>
                {imprimindoOs ? 'Abrindo...' : 'Imprimir OS'}
              </button>
            )}
            <button type="button" className="btn" style={{ background: 'var(--ok)', boxShadow: 'none' }} onClick={() => mudarStatus('em_andamento')}>▶ Iniciar chamado</button>
          </div>
        </div>
      ) : (
        <Passos
          passos={passos}
          rotuloFim={etapasHabilitado && !ehGestorEtapas ? 'Conclua as suas etapas' : 'Finalize o atendimento'}
        />
      )}

      {confirma && createPortal(
        <div onClick={() => setConfirma(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(11,31,77,.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, padding: '24px 26px 18px', width: '100%', maxWidth: 440, boxShadow: '0 30px 70px rgba(11,31,77,.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{ width: 42, height: 42, borderRadius: 12, background: confirma.cor === 'var(--erro)' ? '#fee2e2' : '#dcfce7', color: confirma.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, flexShrink: 0 }}>!</span>
              <h3 style={{ margin: 0, fontSize: 18 }}>{confirma.titulo}</h3>
            </div>
            <div style={{ color: 'var(--texto-suave)', fontSize: 14, lineHeight: 1.65 }}>
              {confirma.linhas.map((l, i) => <div key={i}>{l}</div>)}
            </div>
            <div className="form-actions" style={{ marginTop: 18 }}>
              <button type="button" className="btn sec" onClick={() => setConfirma(null)}>Voltar</button>
              <button type="button" className="btn" style={{ background: confirma.cor, boxShadow: 'none' }} onClick={() => { const a = confirma.acao; setConfirma(null); a(); }}>{confirma.rotulo}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
